import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment-timezone';
import { saveAs } from 'file-saver';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DownloadOutlined as DownloadIcon,
  EditOutlined as EditIcon,
  LinkOutlined as LinkIcon,
  MoreHorizOutlined as MoreIcon,
  RestartAltOutlined as ResetIcon,
  RefreshOutlined as RefreshIcon,
  SyncOutlined as SyncIcon,
  VisibilityOutlined as VisibilityIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { APIService } from 'src/api/api';
import CustomPagination from 'src/common/CustomPagination';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import Loader from 'src/common/Loader';
import ScreenHeader from 'src/common/ScreenHeader';
import useAuth from 'src/context/AuthContext';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import {
  ADDITONAL_ENTRY_REGEX,
  ARTICLE_TYPE,
  CODE_REGEX,
  CONSUMER_VEHICLE_TYPES,
  DATE_FORMAT,
  ENTITY_NAME_REGEX,
  INVOICE_NUM_REGEX,
  NAME_REGEX,
  PIN_NUMBER_REGEX,
  TANK_TYPE,
  TELEMATICS_TYPES,
  VEHICLE_CATEGORY,
  VEHICLE_NUMBER_REGEX,
  VEHICLE_TYPE
} from 'src/utils/constants';
import { getFormattedDate, getFuelQuantity } from 'src/utils/getInitials';
import {
  getFuelVolumeUnitLabel,
  getFuelVolumeUnitShortLabel,
  hasConfiguredFuelVolumeUnit,
  litresToConfiguredUnit
} from 'src/utils/volumeUtils';
import { getStoredUserTimezone, TIMEZONE_OPTIONS } from 'src/utils/timezoneUtils';
import CardSyncStatusModal from 'src/components/cards/CardSyncStatusModal';
import DispenserLinkedCardsModel from 'src/components/dispensers/formModel/DispenserLinkedCardsModel';
import NewMap from 'src/components/dispensers/NewMap';
import { getSupportExportErrorMessage } from './supportExportUtils';

const FEATURE = {
  summary: 'dashboard.summary.view',
  lookups: 'company.switch',
  sites: 'site.view',
  siteCreate: 'site.create',
  siteUpdate: 'site.update',
  issue: 'transactions.issue.view',
  issueUpdate: 'transactions.issue.update',
  inward: 'transactions.inward.view',
  inwardAdd: 'transactions.inward.add',
  inwardUpdate: 'transactions.inward.update',
  transfer: 'transactions.transfer.view',
  external: 'transactions.external.view',
  missed: 'missed_transaction.view',
  vehicles: 'vehicle.view',
  vehicleCreate: 'vehicle.create',
  vehicleUpdate: 'vehicle.update',
  tanks: 'tank.view',
  tankCreate: 'tank.create',
  tankUpdate: 'tank.update',
  dispensers: 'dispenser.view',
  dispenserCreate: 'dispenser.create',
  dispenserUpdate: 'dispenser.update',
  cards: 'card.view',
  cardCreate: 'card.create',
  cardUpdate: 'card.update'
};

const SUPPORT_TABS = [
  { id: 'issue', label: 'Issue', feature: FEATURE.issue },
  { id: 'inward', label: 'Inward', feature: FEATURE.inward },
  { id: 'transfer', label: 'Transfer', feature: FEATURE.transfer },
  { id: 'external', label: 'External', feature: FEATURE.external },
  { id: 'sites', label: 'Sites', feature: FEATURE.sites },
  { id: 'vehicles', label: 'Vehicles', feature: FEATURE.vehicles },
  { id: 'tanks', label: 'Tanks', feature: FEATURE.tanks },
  { id: 'dispensers', label: 'Dispensers', feature: FEATURE.dispensers },
  { id: 'cards', label: 'Cards', feature: FEATURE.cards },
  { id: 'missed', label: 'Missed', feature: FEATURE.missed }
];

const INITIAL_RANGE = {
  start_time: moment().tz(getStoredUserTimezone()).subtract(7, 'days').startOf('day')
    .format(DATE_FORMAT.unixDateTime),
  end_time: moment().tz(getStoredUserTimezone()).format(DATE_FORMAT.unixDateTime)
};

const currentDateTimeInput = () => moment().tz(getStoredUserTimezone()).format('YYYY-MM-DDTHH:mm');

const getExportDateRangePreset = (presetId) => {
  const now = moment().tz(getStoredUserTimezone()).seconds(0).milliseconds(0);
  const today = now.clone().startOf('day');

  if (presetId === 'yesterday') {
    const yesterday = today.clone().subtract(1, 'day');
    return { start: yesterday, end: yesterday.clone().endOf('day') };
  }
  if (presetId === 'last-7-days') {
    return { start: today.clone().subtract(6, 'days'), end: now };
  }
  if (presetId === 'last-30-days') {
    return { start: today.clone().subtract(29, 'days'), end: now };
  }
  return { start: today, end: now };
};

const blankInwardForm = {
  site: '',
  tank: '',
  received_tank: '',
  order_date: currentDateTimeInput(),
  delivery_date: currentDateTimeInput(),
  quantity: '',
  delivered_dip_quantity: '0',
  delivered_measured_quantity_FLS: '0',
  delivered_measured_quantity_meter: '0',
  temperature_during_loading: '0',
  temperature_during_unloading: '0',
  actual_filled: '',
  price_per_litre: '',
  supplier: '',
  invoice_number: '',
  received_station: '',
  spillage_quantity: '0',
  shortage_quantity: '0',
  density_during_loading: '0',
  density_during_unloading: '0',
  delivery_vehicle_number: '',
  remarks: ''
};

const blankIssueForm = {
  odometer_entry: '',
  vehicle_number: '',
  additional_entry: ''
};

const blankSiteForm = {
  name: '',
  code: '',
  address_line1: '',
  city: '',
  state: '',
  pin_code: '',
  country: 'IN',
  timezone: 'Asia/Kolkata',
  active: true
};

const blankVehicleForm = {
  site: '',
  registration_num: '',
  model: '',
  asset_code: '',
  group: '',
  type: '',
  category: '',
  tank: '',
  consumer_vehicle: '',
  fuel_type: '',
  standard_mileage: '',
  tank_capacity: '',
  odometer_reading: '',
  department: '',
  contractor: '',
  telemetics_enabled: false,
  telemetics_category: '',
  fleet_service_provider: '',
  show_omnicomm_log_ignition: true,
  show_omnicomm_log_engine: true,
  show_omnicomm_log_secondary_engine: false,
  omnicomm_secondary_engine_threshold: 8,
  po_number: '',
  monthly_fixed_engine_hours: 0,
  service_interval: 0,
  max_diff_allowed: 0
};

const blankTankForm = {
  site: '',
  type: '',
  article: '',
  name: '',
  code: '',
  sensor_id: '',
  max_volume: 0,
  min_volume: 0,
  max_level: 0,
  min_level: 0,
  automation: false,
  fuel_level_only: false,
  fleet_provider_enabled: false,
  fleet_service_provider: '',
  fleet_sensor_name: '',
  sensor_inward_enabled: false
};

const blankDispenserForm = {
  site: '',
  tank_id: '',
  mode: 'Online Transactions Uploading',
  address: '',
  station_name: '',
  vehicle_number: '',
  kfactor: '0',
  article_unit: 'Litres',
  price: '1',
  article: 'Diesel',
  pulse_type: 'Single',
  pulse_direction: 'Any Direction',
  throttle_volume: '1000',
  throttle_stage: 'Dual Stage',
  min_flow_rate: '10',
  max_flow_rate: '100',
  max_fueling_time: '99999',
  fuel_break_time: '60',
  fuel_start_time: '60',
  odometer_timeout: '60',
  add_input_timeout: '60',
  pin_entry_timeout: '60',
  nozzle_timeout: '0',
  nozzle_id: '0',
  totalizer_start_reading: '0',
  density_start_reading: '0',
  volume_decimal: '2',
  amount_decimal: '2',
  unit_price_decimal: '2',
  atg_connection: false,
  atg_type: 'No ATG',
  tax_percent: '0',
  printer: false,
  location: false,
  card_reader: false,
  fueling_with_id_input: false,
  back_light: true,
  date_time_sync: true,
  date_time: currentDateTimeInput(),
  password1: '',
  password2: '',
  alarm: '0',
  updated_field: '',
  shift_start_time: '',
  shift_duration: '',
  empty_fill_allowed: '99',
  dispenser_mount: 'Mobile',
  dp_no: '1',
  dp_interface: 'Pulse',
  dp_address: '1',
  sequential_dispenser_id: 1,
  kilometer_difference: 1,
  max_digits_of_entry: '0',
  online_connection_timeout: 10,
  max_quantity_per_transaction: 10,
  status: true
};

const blankCardForm = {
  site: '',
  active: true,
  vehicle_id: '',
  dispenser_ids: [],
  operator_id: '',
  vehicle_driver_id: '',
  card_type: 'Positive',
  interface: 'LF',
  uid: '',
  card_category: 'Vehicle Card',
  fill_limit_type: 'Per Filling',
  per_filling_limit: '9999',
  per_day_limit: '9999',
  limit_quantity: '999',
  limit_start_date: null,
  limit_end_date: null,
  fuel_type: 'Diesel',
  pin: '0000',
  wrong_pin_count: '9',
  manual_inputs: '0000',
  product_table: '00000',
  addition_info: '0',
  vehicle_reg_num: '0',
  pin_select: false,
  odometer_info: 'Disable'
};

const numberOrZeroFields = new Set([
  'quantity',
  'delivered_dip_quantity',
  'delivered_measured_quantity_FLS',
  'delivered_measured_quantity_meter',
  'temperature_during_loading',
  'temperature_during_unloading',
  'spillage_quantity',
  'shortage_quantity',
  'density_during_loading',
  'density_during_unloading',
  'actual_filled',
  'price_per_litre',
  'standard_mileage',
  'tank_capacity',
  'odometer_reading',
  'omnicomm_secondary_engine_threshold',
  'monthly_fixed_engine_hours',
  'service_interval',
  'max_diff_allowed',
  'max_volume',
  'min_volume',
  'max_level',
  'min_level',
  'price',
  'throttle_volume',
  'min_flow_rate',
  'max_flow_rate',
  'max_fueling_time',
  'fuel_break_time',
  'fuel_start_time',
  'odometer_timeout',
  'add_input_timeout',
  'pin_entry_timeout',
  'nozzle_timeout',
  'totalizer_start_reading',
  'density_start_reading',
  'volume_decimal',
  'amount_decimal',
  'unit_price_decimal',
  'tax_percent',
  'empty_fill_allowed',
  'dp_no',
  'dp_address',
  'sequential_dispenser_id',
  'kilometer_difference',
  'online_connection_timeout',
  'max_quantity_per_transaction',
  'per_filling_limit',
  'per_day_limit',
  'limit_quantity',
  'wrong_pin_count'
]);

const FILL_LIMIT_TYPES = ['Per Filling', 'Per Day', 'Per Duration', 'One Time Limit'].map((name) => ({ id: name, name }));
const CARD_TYPES = ['Positive', 'Negative'].map((name) => ({ id: name, name }));
const CARD_INTERFACES = ['LF', 'HF', 'WLF', 'Barcode'].map((name) => ({ id: name, name }));
const CARD_CATEGORIES = ['Vehicle Card', 'Driver Card', 'Menu Card', 'Vehicle 2 Card', 'Driver 2 Card'].map((name) => ({ id: name, name }));
const ODOMETER_INFO_OPTIONS = ['Disable', 'Odometer Entry', 'Hour Meter Entry'].map((name) => ({ id: name, name }));
const FLEET_SERVICE_PROVIDER_TYPES = ['WAILON', 'OMNICOMM', 'LOCONAV', 'GEOSAFE', 'VAMOSYS'].map((name) => ({ id: name, name }));
const LEGACY_SEMI_MOVABLE_OPTION = {
  id: 'Semi Movable',
  name: 'Semi Movable'
};
const VEHICLE_TYPE_FORM_OPTIONS = VEHICLE_TYPE.filter((option) => option.id !== LEGACY_SEMI_MOVABLE_OPTION.id);
const TANK_FLEET_SERVICE_PROVIDER_TYPES = ['WAILON', 'OMNICOMM', 'LOCONAV', 'GEOSAFE'].map((name) => ({ id: name, name }));
const DISPENSER_MODES = [
  'Subscription Close',
  'Manual Mode',
  'Online Transactions Uploading',
  'Online Card Authorisation',
  'Offline Cards Management'
].map((name) => ({ id: name, name }));
const PULSE_TYPES = ['Single', 'Dual', 'RS485'].map((name) => ({ id: name, name }));
const PULSE_DIRECTIONS = ['Any Direction', 'Unidirection'].map((name) => ({ id: name, name }));
const THROTTLE_STAGES = ['Single Stage', 'Dual Stage', 'Multistage'].map((name) => ({ id: name, name }));
const ATG_TYPES = ['No ATG', 'Analog', 'RS232', 'RS485'].map((name) => ({ id: name, name }));
const DISPENSER_MOUNTS = ['Mobile', 'Stationary', 'Decant'].map((name) => ({ id: name, name }));
const DP_INTERFACES = ['Tokheim', 'GVR', 'Pulse'].map((name) => ({ id: name, name }));
const MIN_DIGITS_OPTIONS = Array.from({ length: 10 }, (_, index) => ({ id: String(index), name: String(index) }));
const PRODUCT_TABLE_OPTIONS = [
  ['0', 'Petrol'],
  ['1', 'Diesel'],
  ['2', 'Bio-Diesel'],
  ['3', 'Adblue'],
  ['4', 'Lube']
];
const MANUAL_INPUT_OPTIONS = [
  ['0', 'Additional Info'],
  ['1', 'Odometer Info'],
  ['2', 'Vehicle Registration Number'],
  ['3', 'PIN']
];

const selectMenuSx = {
  '& .MuiSelect-select': {
    minHeight: 20
  }
};

const formatValue = (value, type, volumeUnit, fallbackLitres) => {
  if (type === 'date') return getFormattedDate(value);
  if (type === 'litre_quantity') {
    const litreValue = Number(value);
    return Number.isFinite(litreValue) ? `${litreValue.toFixed(2)} L` : 'NA';
  }
  if (type === 'configured_quantity') {
    let configuredValue = Number(value);
    if (!Number.isFinite(configuredValue)) {
      configuredValue = litresToConfiguredUnit(Number(fallbackLitres), volumeUnit);
    }
    if (!Number.isFinite(configuredValue)) return 'NA';
    return `${configuredValue.toFixed(2)} ${getFuelVolumeUnitShortLabel(volumeUnit)}`;
  }
  if (type === 'quantity') return getFuelQuantity(Number(value || 0), undefined, volumeUnit);
  if (typeof value === 'boolean') return value ? 'Active' : 'Inactive';
  if (value === null || value === undefined || value === '') return 'NA';
  if (Array.isArray(value)) {
    const labels = value
      .map((item) => (item && typeof item === 'object' ? item.sync_status || item.status : item))
      .filter(Boolean);
    return labels.length ? [...new Set(labels)].join(', ') : 'NA';
  }
  if (typeof value === 'object') return value.sync_status || value.status || 'NA';
  return value;
};

const formatDetailLabel = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDetailValue = (key, value, volumeUnit) => {
  if (Array.isArray(value)) {
    if (!value.length) return 'NA';
    return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : item)).join(', ');
  }
  if (value && typeof value === 'object') return JSON.stringify(value);
  if (value && /(date|_on|_at|time)/i.test(key)) return getFormattedDate(value);
  if (/(quantity|stock|balance)/i.test(key)) return formatValue(value, 'quantity', volumeUnit);
  return formatValue(value, undefined, volumeUnit);
};

const maxDateTime = () => moment().tz(getStoredUserTimezone()).format(DATE_FORMAT.unixDateTime);

const parseSupportDateTime = (value) => {
  if (!value) return null;
  const parsedValue = moment.tz(
    value,
    [DATE_FORMAT.unixDateTime, DATE_FORMAT.timeStamp],
    true,
    getStoredUserTimezone()
  );
  return parsedValue.isValid() ? parsedValue : null;
};

const formatDateTimeInput = (value, fallback = '') => {
  if (!value) return fallback;
  const parsed = moment(value);
  return parsed.isValid()
    ? parsed.tz(getStoredUserTimezone()).format(DATE_FORMAT.unixDateTime)
    : fallback;
};

const isBlank = (value) => value === null || value === undefined || value === '';

const bitStringHas = (value, index) => String(value || '').charAt(Number(index)) === '1';

const toggleBitString = (value, index, length) => {
  const bits = String(value || '').padEnd(length, '0').slice(0, length).split('');
  bits[Number(index)] = bits[Number(index)] === '1' ? '0' : '1';
  return bits.join('');
};

const requireFields = (form, fields) => {
  const missing = fields.find(([field]) => {
    if (Array.isArray(form[field])) return !form[field].length;
    return isBlank(form[field]);
  });
  return missing ? `${missing[1]} is required` : null;
};

const validateNumbers = (form, fields) => {
  const invalid = fields.find(([field]) => (
    !isBlank(form[field]) && (Number.isNaN(Number(form[field])) || Number(form[field]) < 0)
  ));
  return invalid ? `${invalid[1]} must be a valid non-negative number` : null;
};

const validateMaxNumbers = (form, fields) => {
  const invalid = fields.find(([field, , max]) => (
    !isBlank(form[field]) && Number(form[field]) > max
  ));
  return invalid ? `${invalid[1]} must be less than or equal to ${invalid[2]}` : null;
};

const validateNumberRanges = (form, fields) => {
  const invalid = fields.find(([field, , min, max]) => (
    !isBlank(form[field]) && (Number(form[field]) < min || Number(form[field]) > max)
  ));
  return invalid ? `${invalid[1]} must be between ${invalid[2]} and ${invalid[3]}` : null;
};

const validatePatterns = (form, fields) => {
  const invalid = fields.find(([field, , regex]) => !isBlank(form[field]) && !regex.test(String(form[field])));
  return invalid ? `${invalid[1]} is invalid` : null;
};

const validateMaxLengths = (form, fields) => {
  const invalid = fields.find(([field, , max]) => !isBlank(form[field]) && String(form[field]).length > max);
  return invalid ? `${invalid[1]} must be at most ${invalid[2]} characters` : null;
};

const validateDateRange = (start, end, startLabel = 'Start', endLabel = 'End') => {
  const now = moment();
  if (start && moment(start).isAfter(now)) return `${startLabel} cannot be in the future`;
  if (end && moment(end).isAfter(now)) return `${endLabel} cannot be in the future`;
  if (start && end && moment(start).isAfter(moment(end))) return `${startLabel} must be before ${endLabel}`;
  return null;
};

const validateInwardDateRange = (start, end) => {
  const minDate = moment().subtract(1, 'month');
  if (start && moment(start).isBefore(minDate)) return 'Order Date/Time cannot be older than one month';
  if (end && moment(end).isBefore(minDate)) return 'Delivery Date/Time cannot be older than one month';
  return validateDateRange(start, end, 'Order Date/Time', 'Delivery Date/Time');
};

const validateOrderedDateRange = (start, end, startLabel = 'Start', endLabel = 'End') => {
  if (start && end && moment(start).isAfter(moment(end))) return `${endLabel} must be greater than ${startLabel}`;
  return null;
};

const getSupportValidationError = (type, form, options = {}) => {
  if (type === 'inward') {
    return requireFields(form, [
      ['site', 'Site'],
      ['received_tank', 'Output Tank'],
      ['quantity', 'Fuel Quantity'],
      ['price_per_litre', 'Price Per Litre'],
      ['supplier', 'Supplier'],
      ['invoice_number', 'Invoice Number'],
      ['order_date', 'Order Date/Time'],
      ['delivery_date', 'Delivery Date/Time'],
      ['delivery_vehicle_number', 'Delivery Vehicle Number'],
      ['delivered_dip_quantity', 'Delivered Dip Quantity'],
      ['delivered_measured_quantity_FLS', 'Delivered Measured Quantity FLS'],
      ['delivered_measured_quantity_meter', 'Delivered Measured Quantity Meter'],
      ['temperature_during_loading', 'Temperature During Loading'],
      ['temperature_during_unloading', 'Temperature During Unloading'],
      ['spillage_quantity', 'Spillage Quantity'],
      ['shortage_quantity', 'Shortage Quantity'],
      ['density_during_loading', 'Density During Loading'],
      ['density_during_unloading', 'Density During Unloading']
    ])
      || validatePatterns(form, [
        ['invoice_number', 'Invoice Number', INVOICE_NUM_REGEX],
        ['remarks', 'Remarks', ENTITY_NAME_REGEX],
        ['delivery_vehicle_number', 'Delivery Vehicle Number', VEHICLE_NUMBER_REGEX],
      ])
      || validateMaxLengths(form, [
        ['delivery_vehicle_number', 'Delivery Vehicle Number', 50],
      ])
      || validateNumbers(form, [
        ['quantity', 'Fuel Quantity'],
        ['delivered_dip_quantity', 'Delivered Dip Quantity'],
        ['delivered_measured_quantity_FLS', 'Delivered Measured Quantity FLS'],
        ['delivered_measured_quantity_meter', 'Delivered Measured Quantity Meter'],
        ['temperature_during_loading', 'Temperature During Loading'],
        ['temperature_during_unloading', 'Temperature During Unloading'],
        ['spillage_quantity', 'Spillage Quantity'],
        ['shortage_quantity', 'Shortage Quantity'],
        ['density_during_loading', 'Density During Loading'],
        ['density_during_unloading', 'Density During Unloading'],
        ['price_per_litre', 'Price Per Litre']
      ])
      || validateMaxNumbers(form, [
        ['quantity', 'Fuel Quantity', 99999],
        ['price_per_litre', 'Price Per Litre', 9999],
        ['delivered_dip_quantity', 'Delivered Dip Quantity', 99999],
        ['delivered_measured_quantity_FLS', 'Delivered Measured Quantity FLS', 99999],
        ['delivered_measured_quantity_meter', 'Delivered Measured Quantity Meter', 99999],
        ['temperature_during_loading', 'Temperature During Loading', 999],
        ['temperature_during_unloading', 'Temperature During Unloading', 999],
        ['spillage_quantity', 'Spillage Quantity', 99999],
        ['shortage_quantity', 'Shortage Quantity', 99999],
        ['density_during_loading', 'Density During Loading', 999],
        ['density_during_unloading', 'Density During Unloading', 999],
      ])
      || validateInwardDateRange(form.order_date, form.delivery_date);
  }
  if (type === 'vehicle') {
    const assetCodeMappingEnabled = Boolean(options.assetCodeMappingEnabled);
    const isEdit = Boolean(options.isEdit);
    const baseError = requireFields(form, [
      ['registration_num', 'Registration Number'],
      ['type', 'Vehicle Type'],
      ['asset_code', 'Asset Code'],
      ['tank_capacity', 'Tank Capacity'],
      ['site', 'Site'],
      ['fuel_type', 'Fuel Type'],
      ['odometer_reading', 'Odometer Reading']
    ]);
    if (baseError) return baseError;
    const patternError = validatePatterns(form, [
      ['registration_num', 'Registration Number', VEHICLE_NUMBER_REGEX],
      ['model', 'Vehicle Model', ENTITY_NAME_REGEX],
      ['asset_code', 'Asset Code', assetCodeMappingEnabled && !isEdit ? /^\d+$/ : CODE_REGEX],
    ]);
    if (patternError) return patternError;
    const lengthError = validateMaxLengths(form, [
      ['registration_num', 'Registration Number', 50],
      ['po_number', 'PO Number', 50],
    ]);
    if (lengthError) return lengthError;
    if (!isEdit && assetCodeMappingEnabled && !/^\d{3,4}$/.test(String(form.asset_code))) {
      return 'Asset Code must be exactly 3 or 4 digits';
    }
    if (form.category === 'TRNSFR' && isBlank(form.tank)) return 'Tank selection is required';
    if (form.telemetics_enabled && isBlank(form.telemetics_category)) return 'Telematics Category is required';
    return validateNumbers(form, [
      ['tank_capacity', 'Tank Capacity'],
      ['odometer_reading', 'Odometer Reading'],
      ['standard_mileage', 'Standard Mileage'],
      ['monthly_fixed_engine_hours', 'Monthly Fixed Engine Hours'],
      ['service_interval', 'Service Interval'],
      ['max_diff_allowed', 'Max Diff Allowed'],
      ['omnicomm_secondary_engine_threshold', 'Secondary Engine Threshold']
    ])
      || validateMaxNumbers(form, [
        ['tank_capacity', 'Tank Capacity', 99999],
        ['odometer_reading', 'Odometer Reading', 999999],
        ['standard_mileage', 'Standard Mileage', 99999],
        ['monthly_fixed_engine_hours', 'Monthly Fixed Engine Hours', 99999],
        ['service_interval', 'Service Interval', 99999],
        ['max_diff_allowed', 'Max Diff Allowed', 999],
        ['omnicomm_secondary_engine_threshold', 'Secondary Engine Threshold', 99999]
      ])
      || (!isBlank(form.standard_mileage) && Number(form.standard_mileage) <= 0
        ? 'Standard Mileage must be greater than 0'
        : null);
  }
  if (type === 'tank') {
    const baseError = requireFields(form, [
      ['site', 'Site'],
      ['type', 'Tank Type'],
      ['name', 'Tank Name'],
      ['code', 'Tank Code'],
      ['article', 'Fuel Type'],
      ['sensor_id', 'Sensor Id'],
      ['max_volume', 'Max Volume'],
      ['min_volume', 'Min Volume'],
      ['max_level', 'Max Level'],
      ['min_level', 'Min Level']
    ]);
    if (baseError) return baseError;
    const patternError = validatePatterns(form, [
      ['name', 'Tank Name', ENTITY_NAME_REGEX],
      ['code', 'Tank Code', CODE_REGEX],
      ['sensor_id', 'Sensor Id', CODE_REGEX],
    ]);
    if (patternError) return patternError;
    const lengthError = validateMaxLengths(form, [
      ['name', 'Tank Name', 50],
      ['code', 'Tank Code', 20],
      ['sensor_id', 'Sensor Id', 20],
      ['fleet_sensor_name', 'Sensor Name', 150],
    ]);
    if (lengthError) return lengthError;
    if (String(form.code).length < 4) return 'Tank Code must be at least 4 characters';
    if (String(form.sensor_id).length < 4) return 'Sensor Id must be at least 4 characters';
    if (form.fleet_provider_enabled) {
      const fleetError = requireFields(form, [
        ['fleet_service_provider', 'Fleet Service Provider'],
        ['fleet_sensor_name', 'Sensor Name']
      ]);
      if (fleetError) return fleetError;
    }
    return validateNumbers(form, [
      ['max_volume', 'Max Volume'],
      ['min_volume', 'Min Volume'],
      ['max_level', 'Max Level'],
      ['min_level', 'Min Level']
    ]) || validateMaxNumbers(form, [
      ['max_volume', 'Max Volume', 99999],
      ['min_volume', 'Min Volume', 99999],
      ['max_level', 'Max Level', 99999],
      ['min_level', 'Min Level', 99999]
    ]);
  }
  if (type === 'dispenser') {
    const baseError = requireFields(form, [
      ['site', 'Site'],
      ['tank_id', 'Tank'],
      ['station_name', 'Dispenser Name'],
      ['address', 'Serial Number'],
      ['mode', 'Dispenser Mode'],
      ['article', 'Article'],
      ['article_unit', 'Article Unit'],
      ['price', 'Article Price'],
      ['pulse_type', 'Pulse Type'],
      ['pulse_direction', 'Pulse Direction'],
      ['throttle_stage', 'Throttle Stage'],
      ['atg_type', 'ATG Type'],
      ['date_time', 'Date/Time'],
      ['tax_percent', 'Tax Percent decimal'],
      ['alarm', 'Alarms'],
    ]);
    if (baseError) return baseError;
    return validatePatterns(form, [
      ['station_name', 'Dispenser Name', ENTITY_NAME_REGEX],
      ['address', 'Serial Number', CODE_REGEX],
      ['article_unit', 'Article Unit', NAME_REGEX],
      ['vehicle_number', 'Vehicle Number', VEHICLE_NUMBER_REGEX],
      ['updated_field', 'Updated Field', CODE_REGEX],
      ['password1', 'Password 1', CODE_REGEX],
      ['password2', 'Password 2', CODE_REGEX],
      ['nozzle_id', 'Nozzle ID', CODE_REGEX],
    ])
      || validateMaxLengths(form, [
        ['vehicle_number', 'Vehicle Number', 50],
        ['updated_field', 'Updated Field', 4],
      ])
      || validateNumbers(form, [
        ['price', 'Article Price'],
        ['throttle_volume', 'Throttle Volume'],
        ['min_flow_rate', 'Min Flow Rate'],
        ['max_flow_rate', 'Max Flow Rate'],
        ['max_fueling_time', 'Max Fuelling Time'],
        ['fuel_break_time', 'Fuel Break Time'],
        ['fuel_start_time', 'Fuel Start Time'],
        ['odometer_timeout', 'Odometer Timeout'],
        ['add_input_timeout', 'Add Input Timeout'],
        ['pin_entry_timeout', 'Pin Entry Timeout'],
        ['nozzle_timeout', 'Nozzle Timeout'],
        ['totalizer_start_reading', 'Totalizer Start Reading'],
        ['density_start_reading', 'Density Start Reading'],
        ['volume_decimal', 'Volume Decimal'],
        ['amount_decimal', 'Amount Decimal'],
        ['unit_price_decimal', 'Unit Price Decimal'],
        ['tax_percent', 'Tax Percent'],
        ['empty_fill_allowed', 'Empty Filling Allowed'],
        ['dp_no', 'DP No/Nozzle No'],
        ['dp_address', 'DP Address'],
        ['sequential_dispenser_id', 'Sequential Dispenser ID'],
        ['kilometer_difference', 'Kilometer Difference'],
        ['online_connection_timeout', 'Online Connection Timeout'],
        ['max_quantity_per_transaction', 'Max Quantity Per Transaction']
      ])
      || validateMaxNumbers(form, [
        ['dp_no', 'DP No/Nozzle No', 99],
        ['dp_address', 'DP Address', 99],
        ['totalizer_start_reading', 'Totalizer Start Reading', 99999999999],
        ['density_start_reading', 'Density Start Reading', 9999],
        ['volume_decimal', 'Volume Decimal', 3],
        ['amount_decimal', 'Amount Decimal', 3],
        ['unit_price_decimal', 'Unit Price Decimal', 3],
        ['price', 'Article Price', 999],
        ['throttle_volume', 'Throttle Volume', 9999],
        ['min_flow_rate', 'Min Flow Rate', 9999],
        ['max_flow_rate', 'Max Flow Rate', 9999],
        ['kfactor', 'Meter Factor', 999999],
        ['max_fueling_time', 'Max Fuelling Time', 99999],
        ['fuel_break_time', 'Fuel Break Time', 999],
        ['fuel_start_time', 'Fuel Start Time', 999],
        ['odometer_timeout', 'Odometer Timeout', 999],
        ['add_input_timeout', 'Add Input Timeout', 999],
        ['pin_entry_timeout', 'Pin Entry Timeout', 999],
        ['nozzle_timeout', 'Nozzle Timeout', 999],
        ['tax_percent', 'Tax Percent decimal', 100],
        ['alarm', 'Alarms', 99],
        ['shift_start_time', 'Shift Start Time', 24],
        ['shift_duration', 'Shift Duration', 24],
        ['empty_fill_allowed', 'Empty Filling Allowed', 99],
      ])
      || validateNumberRanges(form, [
        ['sequential_dispenser_id', 'Sequential Dispenser ID', 1, 99],
        ['kilometer_difference', 'Kilometer Difference', 1, 9999],
        ['online_connection_timeout', 'Online Connection Timeout', 10, 9999],
        ['max_quantity_per_transaction', 'Max Quantity Per Transaction', 10, 65000],
      ]);
  }
  if (type === 'card') {
    const baseError = requireFields(form, [
      ['site', 'Site'],
      ['dispenser_ids', 'Dispenser'],
      ['card_type', 'Card Type'],
      ['interface', 'Card Interface'],
      ['card_category', 'Card Category'],
      ['uid', 'Card UID'],
      ['fill_limit_type', 'Per Fill Limit Type'],
      ['per_filling_limit', 'Per Filling Limit'],
      ['per_day_limit', 'Per Day Limit'],
      ['limit_quantity', 'Limit Quantity'],
      ['pin', 'PIN Number'],
      ['wrong_pin_count', 'PIN Error Counter']
    ]);
    if (baseError) return baseError;
    if (!CODE_REGEX.test(String(form.uid))) return 'Card UID is invalid';
    if (String(form.uid).length > 20) return 'Card UID must be at most 20 characters';
    if (!PIN_NUMBER_REGEX.test(String(form.pin))) return 'PIN Number must be exactly 4 digits';
    if (bitStringHas(form.manual_inputs, '0') && !ADDITONAL_ENTRY_REGEX.test(String(form.addition_info))) {
      return 'Additional info is invalid';
    }
    if (bitStringHas(form.manual_inputs, '2') && !ADDITONAL_ENTRY_REGEX.test(String(form.vehicle_reg_num))) {
      return 'Vehicle Reg number is invalid';
    }
    return validateNumbers(form, [
      ['per_filling_limit', 'Per Filling Limit'],
      ['per_day_limit', 'Per Day Limit'],
      ['limit_quantity', 'Limit Quantity'],
      ['wrong_pin_count', 'PIN Error Counter']
    ])
      || validateMaxNumbers(form, [
        ['per_filling_limit', 'Per Filling Limit', 9999],
        ['per_day_limit', 'Per Day Limit', 9999],
        ['limit_quantity', 'Limit Quantity', 999],
        ['wrong_pin_count', 'PIN Error Counter', 99],
      ])
      || (form.fill_limit_type === 'Per Duration' && isBlank(form.limit_start_date)
        ? 'Limit Start Date is required'
        : null)
      || (form.fill_limit_type === 'Per Duration' && isBlank(form.limit_end_date)
        ? 'Limit End Date is required'
        : null)
      || validateOrderedDateRange(form.limit_start_date, form.limit_end_date, 'Limit Start Date', 'Limit End Date');
  }
  return null;
};

const getColumns = (tabId) => {
  const columnsByTab = {
    issue: [
      ['order_id', 'Order'],
      ['start_time', 'Start Time', 'date'],
      ['site_name', 'Site'],
      ['vehicle_number', 'Vehicle'],
      ['card_num_1', 'Card'],
      ['opening_stock', 'Opening Stock', 'quantity'],
      ['closing_stock', 'Closing Stock', 'quantity'],
      ['quantity', 'Quantity', 'quantity'],
    ],
    inward: [
      ['id', 'ID'],
      ['delivery_date', 'Delivery', 'date'],
      ['site_name', 'Site'],
      ['invoice_number', 'Invoice'],
      ['supplier', 'Supplier'],
      ['quantity', 'Quantity', 'quantity'],
      ['received_tank_name', 'Tank'],
    ],
    transfer: [
      ['order_id', 'Order'],
      ['start_time', 'Start Time', 'date'],
      ['site_name', 'Site'],
      ['vehicle_number', 'Vehicle'],
      ['quantity', 'Quantity', 'quantity'],
    ],
    external: [
      ['id', 'ID'],
      ['date_time', 'Date Time', 'date'],
      ['site_name', 'Site'],
      ['vehicle_number', 'Vehicle'],
      ['odometer_entry', 'Odometer'],
      ['quantity', 'Quantity', 'quantity'],
      ['amount', 'Amount'],
    ],
    vehicles: [
      ['registration_num', 'Vehicle'],
      ['site_name', 'Site'],
      ['active', 'Status'],
      ['model', 'Model'],
      ['group_name', 'Group'],
      ['asset_code', 'Asset Code'],
      ['standard_mileage', 'Mileage'],
    ],
    sites: [
      ['name', 'Site'],
      ['code', 'Code'],
      ['city', 'City'],
      ['state', 'State'],
      ['admin_name', 'Site Admin'],
      ['active', 'Status'],
      ['vehicle_count', 'Vehicles'],
      ['tanks_count', 'Tanks'],
      ['dispensers_count', 'Dispensers'],
    ],
    tanks: [
      ['name', 'Tank'],
      ['code', 'Code'],
      ['site_name', 'Site'],
      ['current_balance', 'Current Balance', 'quantity'],
      ['active', 'Status'],
    ],
    dispensers: [
      ['station_name', 'Dispenser'],
      ['address', 'Address'],
      ['site_name', 'Site'],
      ['status', 'Status'],
      ['device_last_synced', 'Last Device Synced', 'date'],
    ],
    cards: [
      ['uid', 'Card UID'],
      ['site_name', 'Site'],
      ['active', 'Status'],
      ['card_category', 'Category'],
      ['fill_limit_type', 'Limit Type'],
      ['limit_quantity', 'Limit Qty', 'quantity'],
      ['vehicle_number', 'Vehicle'],
    ],
    missed: [
      ['number', 'Number'],
      ['transaction_number', 'Transaction'],
      ['address', 'Address'],
      ['dispenser_name', 'Dispenser'],
      ['site_name', 'Site'],
      ['retrieved', 'Retrieved'],
      ['created_at', 'Created', 'date'],
    ],
  };
  return columnsByTab[tabId] || [];
};

const getDisplayColumns = (tabId, company) => {
  const columns = getColumns(tabId);
  if (!hasConfiguredFuelVolumeUnit(company)) return columns;

  const unitLabel = getFuelVolumeUnitLabel(company);
  return columns.flatMap(([key, label, type]) => {
    if (type !== 'quantity') return [[key, label, type]];
    return [
      [key, `${label} (L)`, 'litre_quantity'],
      [`${key}_gallons`, `${label} (${unitLabel})`, 'configured_quantity', key]
    ];
  });
};

const getListFetcher = (tabId) => {
  const fetchers = {
    issue: APIService.getSupportIssueTransactions,
    inward: APIService.getSupportInwardTransactions,
    transfer: APIService.getSupportTransferTransactions,
    external: APIService.getSupportExternalTransactions,
    vehicles: APIService.getSupportVehicles,
    sites: APIService.getSupportSites,
    tanks: APIService.getSupportTanks,
    dispensers: APIService.getSupportDispensers,
    cards: APIService.getSupportCards,
    missed: APIService.getSupportMissedTransactions
  };
  return fetchers[tabId];
};

const getDetailFetcher = (tabId) => {
  const fetchers = {
    vehicles: APIService.getSupportVehicleDetails,
    tanks: APIService.getSupportTankDetails,
    dispensers: APIService.getSupportDispenserDetails,
    cards: APIService.getSupportCardDetails,
    sites: APIService.getSupportSiteDetails,
  };
  return fetchers[tabId];
};

const cleanPayload = (payload) => Object.entries(payload).reduce((acc, [key, value]) => {
  if (value === '') {
    acc[key] = numberOrZeroFields.has(key) ? 0 : null;
    return acc;
  }
  if (numberOrZeroFields.has(key)) {
    acc[key] = Number(value || 0);
    return acc;
  }
  acc[key] = value;
  return acc;
}, {});

const getSupportErrorMessage = (response, fallback) => (
  response?.data?.error_message
  || response?.error_message
  || response?.data?.detail
  || response?.detail
  || Object.values(response?.data || response || {}).find((value) => (
    Array.isArray(value) && value.length && typeof value[0] === 'string'
  ))?.[0]
  || fallback
);

const normalizeVehicleForm = (vehicle) => ({
  ...blankVehicleForm,
  ...(vehicle || {}),
  model: vehicle?.model ?? '',
  contractor: vehicle?.contractor?.id || vehicle?.contractor_id || vehicle?.contractor || '',
  standard_mileage: Number(vehicle?.standard_mileage) > 0 ? vehicle.standard_mileage : '',
  show_omnicomm_log_ignition: vehicle?.show_omnicomm_log_ignition ?? true,
  show_omnicomm_log_engine: vehicle?.show_omnicomm_log_engine ?? true,
  show_omnicomm_log_secondary_engine: vehicle?.show_omnicomm_log_secondary_engine ?? false,
  omnicomm_secondary_engine_threshold: vehicle?.omnicomm_secondary_engine_threshold ?? 8
});

const normalizeTankForm = (tank) => ({
  ...blankTankForm,
  ...(tank || {}),
  fleet_service_provider: tank?.fleet_service_provider ?? '',
  fleet_sensor_name: tank?.fleet_sensor_name ?? ''
});

const normalizeDispenserForm = (dispenser) => ({
  ...blankDispenserForm,
  ...(dispenser || {}),
  date_time: formatDateTimeInput(dispenser?.date_time)
});

const normalizeCardForm = (card) => ({
  ...blankCardForm,
  ...(card || {}),
  site: card?.site || '',
  dispenser_ids: card?.dispenser_ids || [],
  vehicle_id: card?.vehicle_id || '',
  operator_id: card?.operator_id || '',
  vehicle_driver_id: card?.vehicle_driver_id || '',
  wrong_pin_count: card?.wrong_pin_count ?? blankCardForm.wrong_pin_count,
  manual_inputs: card?.manual_inputs || blankCardForm.manual_inputs,
  product_table: card?.product_table || blankCardForm.product_table,
  interface: card?.interface || blankCardForm.interface,
  addition_info: card?.addition_info || blankCardForm.addition_info,
  vehicle_reg_num: card?.vehicle_reg_num || blankCardForm.vehicle_reg_num,
  odometer_info: card?.odometer_info || blankCardForm.odometer_info,
  limit_start_date: formatDateTimeInput(card?.limit_start_date, null),
  limit_end_date: formatDateTimeInput(card?.limit_end_date, null)
});

const normalizeInwardForm = (inward, company) => {
  const volumeFields = [
    'quantity',
    'delivered_dip_quantity',
    'delivered_measured_quantity_FLS',
    'delivered_measured_quantity_meter',
    'spillage_quantity',
    'shortage_quantity'
  ];
  const configuredValues = hasConfiguredFuelVolumeUnit(company)
    ? volumeFields.reduce((values, field) => {
      const configuredField = `${field}_gallons`;
      if (inward?.[configuredField] !== undefined && inward?.[configuredField] !== null) {
        return { ...values, [field]: inward[configuredField] };
      }
      return values;
    }, {})
    : {};

  return {
    ...blankInwardForm,
    ...(inward || {}),
    ...configuredValues,
    site: inward?.site || '',
    tank: inward?.tank || '',
    received_tank: inward?.received_tank || '',
    order_date: formatDateTimeInput(inward?.order_date, currentDateTimeInput()),
    delivery_date: formatDateTimeInput(inward?.delivery_date, currentDateTimeInput()),
    remarks: inward?.remarks || ''
  };
};

const SupportPortal = () => {
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const storedUser = getUser() || {};
  const [companies, setCompanies] = useState(storedUser.companies || []);
  const [permissions, setPermissions] = useState(storedUser.permissions || []);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [dateRange, setDateRange] = useState(INITIAL_RANGE);
  const [siteFilter, setSiteFilter] = useState('all');
  const [vehicleGroupFilter, setVehicleGroupFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('issue');
  const [lookups, setLookups] = useState({
    sites: [],
    suppliers: [],
    site_admins: [],
    tanks: [],
    dispensers: [],
    vehicles: [],
    operators: [],
    vehicle_drivers: [],
    vehicle_groups: [],
    vehicle_departments: [],
    vehicle_contractors: []
  });
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [inwardOpen, setInwardOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState(blankIssueForm);
  const [issueErrors, setIssueErrors] = useState({});
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [tankOpen, setTankOpen] = useState(false);
  const [dispenserOpen, setDispenserOpen] = useState(false);
  const [cardCreateOpen, setCardCreateOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [cardCreateSaving, setCardCreateSaving] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [siteSaving, setSiteSaving] = useState(false);
  const [cardSyncOpen, setCardSyncOpen] = useState(false);
  const [cardSyncRecord, setCardSyncRecord] = useState(null);
  const [linkedCardsOpen, setLinkedCardsOpen] = useState(false);
  const [linkedDispenserId, setLinkedDispenserId] = useState(null);
  const [mapCardsOpen, setMapCardsOpen] = useState(false);
  const [resetDispenserId, setResetDispenserId] = useState(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [cardExporting, setCardExporting] = useState(false);
  const [vehicleExporting, setVehicleExporting] = useState(false);
  const [transactionExporting, setTransactionExporting] = useState(false);
  const [exportFilterOpen, setExportFilterOpen] = useState(false);
  const [exportFilterForm, setExportFilterForm] = useState({
    site: 'all',
    tank: 'all',
    address: 'all',
    vehicle_number: '',
    vehicle_group: 'all',
    asset_type: 'all',
    start_time: INITIAL_RANGE.start_time,
    end_time: INITIAL_RANGE.end_time,
    date_time: currentDateTimeInput()
  });
  const [moreTabsAnchor, setMoreTabsAnchor] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [inwardForm, setInwardForm] = useState(blankInwardForm);
  const [vehicleForm, setVehicleForm] = useState(blankVehicleForm);
  const [tankForm, setTankForm] = useState(blankTankForm);
  const [dispenserForm, setDispenserForm] = useState(blankDispenserForm);
  const [newCardForm, setNewCardForm] = useState(blankCardForm);
  const [cardForm, setCardForm] = useState(null);
  const [siteForm, setSiteForm] = useState(blankSiteForm);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const cardFormSite = cardForm?.site;

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  const hasPermission = useCallback((feature) => permissionSet.has(feature), [permissionSet]);

  const availableTabs = useMemo(
    () => SUPPORT_TABS.filter((tab) => hasPermission(tab.feature)),
    [hasPermission]
  );
  const isSmallScreen = useMediaQuery('(max-width:899px)');
  const visibleTabCount = isSmallScreen ? 4 : 5;
  const visibleTabs = useMemo(
    () => availableTabs.slice(0, visibleTabCount),
    [availableTabs, visibleTabCount]
  );
  const overflowTabs = useMemo(
    () => availableTabs.slice(visibleTabCount),
    [availableTabs, visibleTabCount]
  );
  const activeOverflowTab = overflowTabs.find((tab) => tab.id === activeTab);
  const tabsToRender = activeOverflowTab
    ? [...visibleTabs, activeOverflowTab]
    : visibleTabs;
  const activeTabLabel = SUPPORT_TABS.find((tab) => tab.id === activeTab)?.label || '';

  const selectedCompanyInfo = useMemo(
    () => companies.find((company) => String(company.id) === String(selectedCompany)),
    [companies, selectedCompany]
  );
  const inwardVolumeLabel = hasConfiguredFuelVolumeUnit(selectedCompanyInfo)
    ? ` (${getFuelVolumeUnitLabel(selectedCompanyInfo)})`
    : '';

  const siteOptions = useMemo(
    () => [{ id: 'all', name: 'All Sites' }, ...lookups.sites],
    [lookups.sites]
  );

  const fetchSupportLinkedCards = useCallback(
    (dispenserId, pageLimit, pageNo, params) => APIService.getSupportDispenserLinkedCards(
      selectedCompany,
      dispenserId,
      pageLimit,
      pageNo,
      params
    ),
    [selectedCompany]
  );

  const inwardTankOptions = useMemo(() => (
    inwardForm.site
      ? lookups.tanks.filter((tank) => String(tank.site) === String(inwardForm.site))
      : lookups.tanks
  ), [inwardForm.site, lookups.tanks]);

  const vehicleTransferTankOptions = useMemo(() => (
    vehicleForm.site
      ? lookups.tanks.filter((tank) => String(tank.site) === String(vehicleForm.site))
      : lookups.tanks
  ), [lookups.tanks, vehicleForm.site]);

  const dispenserTankOptions = useMemo(() => (
    dispenserForm.site
      ? lookups.tanks.filter((tank) => String(tank.site) === String(dispenserForm.site))
      : lookups.tanks
  ), [dispenserForm.site, lookups.tanks]);

  const exportTankOptions = useMemo(() => {
    const tanks = exportFilterForm.site && exportFilterForm.site !== 'all'
      ? lookups.tanks.filter((tank) => String(tank.site) === String(exportFilterForm.site))
      : lookups.tanks;
    return [{ id: 'all', name: 'All Tanks' }, ...tanks];
  }, [exportFilterForm.site, lookups.tanks]);

  const exportDispenserOptions = useMemo(() => {
    const dispensers = exportFilterForm.site && exportFilterForm.site !== 'all'
      ? lookups.dispensers.filter((dispenser) => String(dispenser.site) === String(exportFilterForm.site))
      : lookups.dispensers;
    return [{ id: 'all', name: 'All' }, ...dispensers];
  }, [exportFilterForm.site, lookups.dispensers]);

  const exportVehicleOptions = useMemo(() => {
    const vehicles = exportFilterForm.site && exportFilterForm.site !== 'all'
      ? lookups.vehicles.filter((vehicle) => String(vehicle.site) === String(exportFilterForm.site))
      : lookups.vehicles;
    return [{ id: 'all', name: 'All' }, ...vehicles.map((vehicle) => ({
      ...vehicle,
      name: vehicle.registration_num
    }))];
  }, [exportFilterForm.site, lookups.vehicles]);

  const newCardDispenserOptions = useMemo(() => (
    newCardForm.site
      ? lookups.dispensers.filter((dispenser) => String(dispenser.site) === String(newCardForm.site))
      : lookups.dispensers
  ), [lookups.dispensers, newCardForm.site]);

  const newCardVehicleOptions = useMemo(() => (
    newCardForm.site
      ? lookups.vehicles.filter((vehicle) => String(vehicle.site) === String(newCardForm.site))
      : lookups.vehicles
  ), [lookups.vehicles, newCardForm.site]);

  const newCardOperatorOptions = useMemo(() => (
    newCardForm.site
      ? lookups.operators.filter((operator) => String(operator.site) === String(newCardForm.site))
      : lookups.operators
  ), [lookups.operators, newCardForm.site]);

  const newCardDriverOptions = useMemo(() => (
    newCardForm.site
      ? lookups.vehicle_drivers.filter((driver) => String(driver.site) === String(newCardForm.site))
      : lookups.vehicle_drivers
  ), [lookups.vehicle_drivers, newCardForm.site]);

  const cardFormDispenserOptions = useMemo(() => (
    cardFormSite
      ? lookups.dispensers.filter((dispenser) => String(dispenser.site) === String(cardFormSite))
      : lookups.dispensers
  ), [cardFormSite, lookups.dispensers]);

  const cardFormVehicleOptions = useMemo(() => (
    cardFormSite
      ? lookups.vehicles.filter((vehicle) => String(vehicle.site) === String(cardFormSite))
      : lookups.vehicles
  ), [cardFormSite, lookups.vehicles]);

  const cardFormOperatorOptions = useMemo(() => (
    cardFormSite
      ? lookups.operators.filter((operator) => String(operator.site) === String(cardFormSite))
      : lookups.operators
  ), [cardFormSite, lookups.operators]);

  const cardFormDriverOptions = useMemo(() => (
    cardFormSite
      ? lookups.vehicle_drivers.filter((driver) => String(driver.site) === String(cardFormSite))
      : lookups.vehicle_drivers
  ), [cardFormSite, lookups.vehicle_drivers]);

  const listParams = useMemo(() => {
    const params = {
      site: siteFilter === 'all' ? null : siteFilter,
      start_time: dateRange.start_time,
      end_time: dateRange.end_time
    };
    if (activeTab === 'issue' || activeTab === 'transfer' || activeTab === 'external') {
      params.vehicle_number = searchText;
    } else if (['inward', 'vehicles', 'cards', 'tanks', 'dispensers', 'missed'].includes(activeTab)) {
      params.search = searchText;
    }
    if (activeTab === 'vehicles') params.group = vehicleGroupFilter;
    return params;
  }, [activeTab, dateRange, searchText, siteFilter, vehicleGroupFilter]);

  const loadIdentity = useCallback(async () => {
    const response = await APIService.getSupportMe();
    if (response?.success) {
      setCompanies(response.result?.companies || []);
      setPermissions(response.result?.permissions || []);
    }
  }, []);

  const loadCompanyContext = useCallback(async () => {
    if (!selectedCompany) return;
    const rangeError = validateDateRange(dateRange.start_time, dateRange.end_time);
    if (rangeError) {
      dispatch(setErrorInfo({ errorMsg: rangeError }));
      return;
    }
    setLoading(true);
    try {
      const requests = [];
      requests.push(APIService.getSupportLookups(selectedCompany));
      requests.push(hasPermission(FEATURE.summary) ? APIService.getSupportSummary(selectedCompany, dateRange) : Promise.resolve(null));
      const [lookupResponse, summaryResponse] = await Promise.all(requests);
      if (lookupResponse?.success) {
        setLookups({
          ...(lookupResponse.result || {}),
          suppliers: (lookupResponse.result?.suppliers || []).map((supplier) => ({
            id: supplier.name,
            name: supplier.name
          }))
        });
      }
      if (summaryResponse?.success) {
        setSummary(summaryResponse.result || null);
      }
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: 'Unable to load support company context' }));
    } finally {
      setLoading(false);
    }
  }, [dateRange, dispatch, hasPermission, selectedCompany]);

  const loadRows = useCallback(async (nextPage = 0) => {
    if (!selectedCompany || !activeTab) return;
    const rangeError = validateDateRange(dateRange.start_time, dateRange.end_time);
    if (rangeError) {
      dispatch(setErrorInfo({ errorMsg: rangeError }));
      return;
    }
    const fetcher = getListFetcher(activeTab);
    if (!fetcher) return;
    setTableLoading(true);
    try {
      const response = await fetcher(selectedCompany, limit, nextPage + 1, listParams);
      if (!response?.success) throw response;
      setRows(response.result?.results || []);
      setTotalCount(response.result?.count || 0);
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      dispatch(setErrorInfo({ errorMsg: 'Unable to load support records' }));
    } finally {
      setTableLoading(false);
    }
  }, [activeTab, dateRange, dispatch, limit, listParams, selectedCompany]);

  useEffect(() => {
    loadIdentity();
  }, [loadIdentity]);

  useEffect(() => {
    if (!companies.length) {
      setSelectedCompany('');
      return;
    }
    const selectedStillAvailable = companies.some((company) => String(company.id) === String(selectedCompany));
    if (!selectedStillAvailable) {
      setSelectedCompany(companies[0].id);
    }
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (!availableTabs.length) return;
    if (!availableTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, dateRange, searchText, selectedCompany, siteFilter, vehicleGroupFilter]);

  useEffect(() => {
    loadCompanyContext();
  }, [loadCompanyContext]);

  useEffect(() => {
    loadRows(0);
  }, [loadRows]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    loadRows(newPage);
  };

  const updateDateRange = (field, value) => {
    setDateRange((currentRange) => ({ ...currentRange, [field]: value }));
  };

  const openInwardDialog = (inward = null) => {
    setEditingRecord(inward ? { type: 'inward', id: inward.id } : null);
    setInwardForm(normalizeInwardForm(inward, selectedCompanyInfo));
    setInwardOpen(true);
  };

  const openIssueDialog = (issue) => {
    setEditingRecord({ type: 'issue', id: issue.id });
    setIssueForm({
      odometer_entry: issue.odometer_entry ?? '',
      vehicle_number: issue.vehicle_number || '',
      additional_entry: issue.additional_entry ?? ''
    });
    setIssueErrors({});
    setIssueOpen(true);
  };

  const handleSaveIssue = async () => {
    const assetCodeMappingEnabled = Boolean(selectedCompanyInfo?.asset_code_mapping_enabled);
    const vehicleNumber = String(issueForm.vehicle_number || '').trim();
    const odometer = String(issueForm.odometer_entry ?? '').trim();
    const additionalEntry = String(issueForm.additional_entry ?? '').trim();
    const validationErrors = {};
    if (odometer === '' || !/^\d+(\.\d+)?$/.test(odometer) || Number(odometer) < 0) {
      validationErrors.odometer_entry = 'Odometer reading must be a non-negative number';
    }
    if (vehicleNumber === '') {
      validationErrors.vehicle_number = 'Vehicle number is required';
    }
    if (assetCodeMappingEnabled && additionalEntry === '') {
      validationErrors.additional_entry = 'Additional entry is required';
    }
    if (Object.keys(validationErrors).length) {
      setIssueErrors(validationErrors);
      return;
    }
    const payload = {
      odometer_entry: Number(odometer),
      vehicle_number: vehicleNumber
    };
    if (assetCodeMappingEnabled) {
      payload.additional_entry = additionalEntry;
    }
    const response = await APIService.updateSupportIssueTransaction(
      selectedCompany,
      editingRecord.id,
      payload
    );
    const responseData = response?.data || response;
    if (!response?.success || response?.status >= 400) {
      const fieldErrors = ['odometer_entry', 'vehicle_number', 'additional_entry'].reduce((errors, field) => {
        const value = responseData?.[field];
        return value
          ? { ...errors, [field]: Array.isArray(value) ? value[0] : value }
          : errors;
      }, {});
      if (Object.keys(fieldErrors).length) {
        setIssueErrors(fieldErrors);
      } else {
        dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Failed to update transaction') }));
      }
      return;
    }
    setIssueOpen(false);
    setEditingRecord(null);
    setIssueForm(blankIssueForm);
    setIssueErrors({});
    dispatch(setSuccessInfo({ successMsg: 'Transaction updated successfully' }));
    loadRows(0);
  };

  const handleSaveInward = async () => {
    const validationError = getSupportValidationError('inward', inwardForm);
    if (validationError) {
      dispatch(setErrorInfo({ errorMsg: validationError }));
      return;
    }
    const payload = cleanPayload({
      ...inwardForm,
      actual_filled: Number(inwardForm.quantity || 0) - Number(inwardForm.shortage_quantity || 0)
    });
    const response = editingRecord?.type === 'inward'
      ? await APIService.updateSupportInwardTransaction(selectedCompany, editingRecord.id, payload)
      : await APIService.createSupportInwardTransaction(selectedCompany, payload);
    if (!response?.success) {
      dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Failed to update transaction') }));
      return;
    }
    setInwardOpen(false);
    setInwardForm(normalizeInwardForm());
    setEditingRecord(null);
    dispatch(setSuccessInfo({
      successMsg: editingRecord?.type === 'inward'
        ? 'Transaction updated successfully'
        : 'Transaction created successfully'
    }));
    loadCompanyContext();
    loadRows(0);
  };

  const handleSaveVehicle = async () => {
    const validationError = getSupportValidationError('vehicle', vehicleForm, {
      assetCodeMappingEnabled: selectedCompanyInfo?.asset_code_mapping_enabled,
      isEdit: editingRecord?.type === 'vehicles'
    });
    if (validationError) {
      dispatch(setErrorInfo({ errorMsg: validationError }));
      return;
    }
    const payload = cleanPayload(vehicleForm);
    const standardMileageValue = Number(vehicleForm.standard_mileage);
    if (
      !vehicleForm.standard_mileage
      || Number.isNaN(standardMileageValue)
      || standardMileageValue <= 0
    ) {
      delete payload.standard_mileage;
    }
    const response = editingRecord?.type === 'vehicles'
      ? await APIService.updateSupportVehicle(selectedCompany, editingRecord.id, payload)
      : await APIService.createSupportVehicle(selectedCompany, payload);
    if (!response?.success) {
      dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Something went wrong') }));
      return;
    }
    setVehicleOpen(false);
    setVehicleForm(blankVehicleForm);
    setEditingRecord(null);
    dispatch(setSuccessInfo({
      successMsg: editingRecord?.type === 'vehicles'
        ? 'Vehicle details updated successfully!'
        : 'New vehicle has been created successfully!'
    }));
    loadCompanyContext();
    loadRows(0);
  };

  const handleSaveTank = async () => {
    const validationError = getSupportValidationError('tank', tankForm);
    if (validationError) {
      dispatch(setErrorInfo({ errorMsg: validationError }));
      return;
    }
    const payload = cleanPayload({
      ...tankForm,
      sensor_inward_enabled: tankForm.fleet_provider_enabled ? tankForm.sensor_inward_enabled : false,
      fleet_service_provider: tankForm.fleet_provider_enabled ? tankForm.fleet_service_provider : '',
      fleet_sensor_name: tankForm.fleet_provider_enabled ? tankForm.fleet_sensor_name : ''
    });
    const response = editingRecord?.type === 'tanks'
      ? await APIService.updateSupportTank(selectedCompany, editingRecord.id, payload)
      : await APIService.createSupportTank(selectedCompany, payload);
    if (!response?.success) {
      dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Something went wrong') }));
      return;
    }
    setTankOpen(false);
    setTankForm(blankTankForm);
    setEditingRecord(null);
    dispatch(setSuccessInfo({
      successMsg: editingRecord?.type === 'tanks'
        ? 'Tank updated successfully'
        : 'New tank created successfully'
    }));
    loadCompanyContext();
    loadRows(0);
  };

  const handleSaveDispenser = async () => {
    const validationError = getSupportValidationError('dispenser', dispenserForm);
    if (validationError) {
      dispatch(setErrorInfo({ errorMsg: validationError }));
      return;
    }
    const payload = cleanPayload(dispenserForm);
    const response = editingRecord?.type === 'dispensers'
      ? await APIService.updateSupportDispenser(selectedCompany, editingRecord.id, payload)
      : await APIService.createSupportDispenser(selectedCompany, payload);
    if (!response?.success) {
      dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Something went wrong') }));
      return;
    }
    setDispenserOpen(false);
    setDispenserForm({ ...blankDispenserForm, date_time: currentDateTimeInput() });
    setEditingRecord(null);
    dispatch(setSuccessInfo({
      successMsg: editingRecord?.type === 'dispensers'
        ? 'Dispenser updated successfully!'
        : 'New Dispenser created successfully!'
    }));
    loadCompanyContext();
    loadRows(0);
  };

  const handleSaveSite = async () => {
    const missingSiteField = ['name', 'code', 'address_line1', 'city', 'state', 'pin_code', 'country', 'timezone']
      .find((field) => !String(siteForm[field] || '').trim());
    if (missingSiteField) {
      dispatch(setErrorInfo({ errorMsg: 'Please complete all required site fields' }));
      return;
    }
    if (!CODE_REGEX.test(String(siteForm.code).trim()) || String(siteForm.code).trim().length < 4) {
      dispatch(setErrorInfo({ errorMsg: 'Site Code must be at least 4 characters and contain only letters or numbers' }));
      return;
    }
    setSiteSaving(true);
    try {
      const payload = { ...siteForm };
      const response = editingRecord?.type === 'sites'
        ? await APIService.updateSupportSite(selectedCompany, editingRecord.id, payload)
        : await APIService.createSupportSite(selectedCompany, payload);
      if (!response?.success) {
        dispatch(setErrorInfo({ errorMsg: 'Unable to save site' }));
        return;
      }
      setSiteOpen(false);
      setSiteForm(blankSiteForm);
      setEditingRecord(null);
      dispatch(setSuccessInfo({ successMsg: editingRecord?.type === 'sites' ? 'Site updated' : 'Site created' }));
      loadCompanyContext();
      loadRows(0);
    } finally {
      setSiteSaving(false);
    }
  };

  const openVehicleDialog = (vehicle = null) => {
    setEditingRecord(vehicle ? { type: 'vehicles', id: vehicle.id } : null);
    setVehicleForm(vehicle ? normalizeVehicleForm(vehicle) : blankVehicleForm);
    setVehicleOpen(true);
  };

  const openTankDialog = (tank = null) => {
    setEditingRecord(tank ? { type: 'tanks', id: tank.id } : null);
    setTankForm(tank ? normalizeTankForm(tank) : blankTankForm);
    setTankOpen(true);
  };

  const openDispenserDialog = (dispenser = null) => {
    setEditingRecord(dispenser ? { type: 'dispensers', id: dispenser.id } : null);
    setDispenserForm(dispenser ? normalizeDispenserForm(dispenser) : blankDispenserForm);
    setDispenserOpen(true);
  };

  const openSiteDialog = (site = null) => {
    setEditingRecord(site ? { type: 'sites', id: site.id } : null);
    setSiteForm(site ? {
      ...blankSiteForm,
      ...site,
      country: site.country === 'India' ? 'IN' : site.country
    } : blankSiteForm);
    setSiteOpen(true);
  };

  const openDetailDialog = async (tabId, row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRecord({ type: tabId, data: row });
    const fetcher = getDetailFetcher(tabId);
    if (!fetcher || !row?.id) {
      setDetailLoading(false);
      return;
    }
    try {
      const response = await fetcher(selectedCompany, row.id);
      if (response?.success) {
        setDetailRecord({ type: tabId, data: response.result || row });
      }
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: 'Unable to load details' }));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateCard = async () => {
    if (cardCreateSaving) return;
    const validationError = getSupportValidationError('card', newCardForm);
    if (validationError) {
      dispatch(setErrorInfo({ errorMsg: validationError }));
      return;
    }
    setCardCreateSaving(true);
    try {
      const response = await APIService.createSupportCard(
        selectedCompany,
        cleanPayload(newCardForm)
      );
      if (!response?.success) {
        dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Something went wrong') }));
        return;
      }
      setCardCreateOpen(false);
      setNewCardForm(blankCardForm);
      dispatch(setSuccessInfo({ successMsg: 'New Card has been created successfully!' }));
      loadCompanyContext();
      loadRows(0);
    } finally {
      setCardCreateSaving(false);
    }
  };

  const openCardDialog = (card) => {
    setCardForm(normalizeCardForm(card));
    setCardOpen(true);
  };

  const handleUpdateCard = async () => {
    const validationError = getSupportValidationError('card', cardForm);
    if (validationError) {
      dispatch(setErrorInfo({ errorMsg: validationError }));
      return;
    }
    const payload = cleanPayload({
      site: cardForm.site || null,
      active: cardForm.active,
      card_type: cardForm.card_type,
      card_category: cardForm.card_category,
      fill_limit_type: cardForm.fill_limit_type,
      per_filling_limit: cardForm.per_filling_limit,
      per_day_limit: cardForm.per_day_limit,
      limit_quantity: cardForm.limit_quantity,
      limit_start_date: cardForm.limit_start_date,
      limit_end_date: cardForm.limit_end_date,
      fuel_type: cardForm.fuel_type,
      pin: cardForm.pin,
      wrong_pin_count: cardForm.wrong_pin_count,
      manual_inputs: cardForm.manual_inputs,
      product_table: cardForm.product_table,
      interface: cardForm.interface,
      addition_info: cardForm.addition_info,
      odometer_info: cardForm.odometer_info,
      vehicle_reg_num: cardForm.vehicle_reg_num,
      pin_select: cardForm.pin_select,
      vehicle_id: cardForm.vehicle_id,
      operator_id: cardForm.operator_id,
      vehicle_driver_id: cardForm.vehicle_driver_id,
      dispenser_ids: cardForm.dispenser_ids || []
    });
    const response = await APIService.updateSupportCard(selectedCompany, cardForm.id, payload);
    if (!response?.success) {
      dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Something went wrong') }));
      return;
    }
    setCardOpen(false);
    setCardForm(null);
    dispatch(setSuccessInfo({ successMsg: 'Card details updated successfully!' }));
    loadRows(page);
  };

  const openCardSyncStatus = async (card) => {
    setCardSyncRecord(card);
    setCardSyncOpen(true);
    const response = await APIService.getSupportCardDetails(selectedCompany, card.id);
    if (response?.success) setCardSyncRecord(response.result || card);
  };

  const removeSupportCardDispenser = async (mapperId) => {
    const response = await APIService.removeSupportCardDispenser(selectedCompany, mapperId);
    if (!response?.success) throw response;
    setCardSyncRecord((current) => ({
      ...current,
      sync_status: (current?.sync_status || []).filter((item) => item.mapper_id !== mapperId)
    }));
    dispatch(setSuccessInfo({ successMsg: 'The dispenser has been unlinked from the card' }));
    loadRows(page);
  };

  const handleResetDispenserCards = async () => {
    const response = await APIService.resetSupportDispenserCards(selectedCompany, resetDispenserId);
    setResetConfirmOpen(false);
    setResetDispenserId(null);
    if (!response?.success) {
      dispatch(setErrorInfo({ errorMsg: getSupportErrorMessage(response, 'Failed to reset dispenser cards') }));
      return;
    }
    dispatch(setSuccessInfo({ successMsg: 'Dispenser cards reset successfully!' }));
    loadCompanyContext();
    loadRows(page);
  };

  const handleMapSupportCards = async (values) => {
    const response = await APIService.mapSupportCardsToDispenser(selectedCompany, values);
    if (!response?.success) throw response;
  };

  const handleExportCards = async () => {
    setCardExporting(true);
    try {
      const response = await APIService.exportSupportCards(selectedCompany, searchText ? { search: searchText } : {});
      if (response.status !== 200) throw response;
      saveAs(response.data, `support-cards-${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`);
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: getSupportExportErrorMessage(error, 'Unable to export cards')
      }));
    } finally {
      setCardExporting(false);
    }
  };

  const handleSupportDispenserClick = async (dispenserId) => {
    setCardSyncOpen(false);
    setCardSyncRecord(null);
    const dispenser = lookups.dispensers.find((item) => String(item.id) === String(dispenserId));
    if (dispenser) {
      openDetailDialog('dispensers', dispenser);
      return;
    }
    const response = await APIService.getSupportDispenserDetails(selectedCompany, dispenserId);
    if (response?.success) openDetailDialog('dispensers', response.result);
  };

  const handleExportVehicles = async () => {
    setVehicleExporting(true);
    try {
      const params = {
        site: siteFilter === 'all' ? null : siteFilter,
        search: searchText || null,
        group: vehicleGroupFilter === 'all' ? null : vehicleGroupFilter
      };
      const response = await APIService.exportSupportVehicles(selectedCompany, params);
      if (response.status !== 200) throw response;
      saveAs(response.data, `support-vehicles-${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`);
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: getSupportExportErrorMessage(error, 'Unable to export vehicles')
      }));
    } finally {
      setVehicleExporting(false);
    }
  };

  const handleExportTransactions = async () => {
    const rangeError = validateDateRange(exportFilterForm.start_time, exportFilterForm.end_time);
    if (rangeError) {
      dispatch(setErrorInfo({ errorMsg: rangeError }));
      return;
    }
    const exporters = {
      issue: APIService.exportSupportIssueTransactions,
      inward: APIService.exportSupportInwardTransactions,
      transfer: APIService.exportSupportTransferTransactions,
      external: APIService.exportSupportExternalTransactions
    };
    const exporter = exporters[activeTab];
    if (!exporter) return;
    setTransactionExporting(true);
    try {
      const params = Object.entries(exportFilterForm).reduce((result, [key, value]) => (
        value && value !== 'all' ? { ...result, [key]: value } : result
      ), {});
      if (activeTab === 'external') {
        delete params.start_time;
        delete params.end_time;
      } else {
        delete params.date_time;
      }
      const response = await exporter(selectedCompany, params);
      if (response.status !== 200) throw response;
      saveAs(response.data, `support-${activeTab}-transactions-${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`);
      setExportFilterOpen(false);
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: getSupportExportErrorMessage(
          error,
          `Unable to export ${activeTab} transactions`
        )
      }));
    } finally {
      setTransactionExporting(false);
    }
  };

  const openTransactionExport = () => {
    setExportFilterForm((currentForm) => ({
      ...currentForm,
      site: siteFilter,
      tank: currentForm.tank || 'all',
      address: currentForm.address || 'all',
      vehicle_number: currentForm.vehicle_number || 'all',
      vehicle_group: currentForm.vehicle_group || 'all',
      asset_type: currentForm.asset_type || 'all',
      start_time: currentForm.start_time || dateRange.start_time,
      end_time: currentForm.end_time || dateRange.end_time,
      date_time: currentForm.date_time || currentDateTimeInput()
    }));
    setExportFilterOpen(true);
  };

  const applyExportDatePreset = (presetId) => {
    const range = getExportDateRangePreset(presetId);
    setExportFilterForm((currentForm) => ({
      ...currentForm,
      start_time: range.start.format(DATE_FORMAT.unixDateTime),
      end_time: range.end.format(DATE_FORMAT.unixDateTime)
    }));
  };

  const renderMetric = (label, value, tone = 'default') => (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 2,
        minHeight: 92,
        bgcolor: tone === 'strong' ? '#F0F7FF' : '#FFFFFF'
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.75, letterSpacing: 0 }}>
        {value}
      </Typography>
    </Paper>
  );

  const renderSummary = () => {
    if (!summary || !hasPermission(FEATURE.summary)) return null;
    return (
      <Grid container spacing={1.5}>
        <Grid item xs={6} md={3}>
          {renderMetric('Issue Quantity', getFuelQuantity(
            summary.totals?.issue_quantity,
            undefined,
            selectedCompanyInfo?.fuel_volume_unit
          ), 'strong')}
        </Grid>
        <Grid item xs={6} md={3}>
          {renderMetric('Inward Quantity', getFuelQuantity(
            summary.totals?.inward_quantity,
            undefined,
            selectedCompanyInfo?.fuel_volume_unit
          ))}
        </Grid>
        <Grid item xs={6} md={3}>{renderMetric('Vehicles', summary.counts?.vehicles || 0)}</Grid>
        <Grid item xs={6} md={3}>{renderMetric('Pending Missed', summary.counts?.missed_pending || 0)}</Grid>
      </Grid>
    );
  };

  const renderToolbarActions = () => (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
      {activeTab === 'inward' && hasPermission(FEATURE.inwardAdd) && (
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => openInwardDialog()} sx={{ textTransform: 'none' }}>
          Add Inward
        </Button>
      )}
      {['issue', 'inward', 'transfer', 'external'].includes(activeTab) && (
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={openTransactionExport}
          disabled={transactionExporting}
          sx={{ textTransform: 'none' }}
        >
          {transactionExporting ? 'Exporting...' : 'Export'}
        </Button>
      )}
      {activeTab === 'vehicles' && hasPermission(FEATURE.vehicleCreate) && (
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => openVehicleDialog()} sx={{ textTransform: 'none' }}>
          Add Vehicle
        </Button>
      )}
      {activeTab === 'tanks' && hasPermission(FEATURE.tankCreate) && (
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => openTankDialog()} sx={{ textTransform: 'none' }}>
          Add Tank
        </Button>
      )}
      {activeTab === 'dispensers' && hasPermission(FEATURE.dispenserCreate) && (
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDispenserDialog()} sx={{ textTransform: 'none' }}>
          Add Dispenser
        </Button>
      )}
      {activeTab === 'cards' && hasPermission(FEATURE.cardCreate) && (
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCardCreateOpen(true)} sx={{ textTransform: 'none' }}>
          Add Card
        </Button>
      )}
      {activeTab === 'cards' && hasPermission(FEATURE.cards) && (
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={handleExportCards}
          disabled={cardExporting}
          sx={{ textTransform: 'none' }}
        >
          {cardExporting ? 'Exporting...' : 'Export'}
        </Button>
      )}
      {activeTab === 'vehicles' && hasPermission(FEATURE.vehicles) && (
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={handleExportVehicles}
          disabled={vehicleExporting}
          sx={{ textTransform: 'none' }}
        >
          {vehicleExporting ? 'Exporting...' : 'Export'}
        </Button>
      )}
      {activeTab === 'dispensers' && hasPermission(FEATURE.dispenserUpdate) && (
        <Button startIcon={<LinkIcon />} variant="outlined" onClick={() => setMapCardsOpen(true)} sx={{ textTransform: 'none' }}>
          Map Cards
        </Button>
      )}
      {activeTab === 'sites' && hasPermission(FEATURE.siteCreate) && (
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => openSiteDialog()} sx={{ textTransform: 'none' }}>
          Add Site
        </Button>
      )}
      <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => { loadCompanyContext(); loadRows(page); }} sx={{ textTransform: 'none' }}>
        Refresh
      </Button>
    </Stack>
  );

  const searchPlaceholderByTab = {
    issue: 'Search vehicle number',
    inward: 'Search invoice number',
    transfer: 'Search vehicle number',
    external: 'Search vehicle number',
    vehicles: 'Search registration number',
    cards: 'Search Card / Vehicle',
    tanks: 'Search tank name or code',
    dispensers: 'Search dispenser or address',
    sites: 'Search site name, code or city',
    missed: 'Search address or transaction number'
  };

  const renderSelectField = (form, setForm, field, label, options, config = {}) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={config.multiple}
        label={label}
        value={form[field] ?? ''}
        disabled={config.disabled}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 320
            }
          }
        }}
        onChange={(event) => setForm({
          ...form,
          [field]: event.target.value,
          ...(config.resetOnChange || {})
        })}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name || option.registration_num || option.station_name || option.address || option.code}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderTextInput = (form, setForm, field, label, type = 'text', extraProps = {}) => {
    const { allowFuture, inputProps, ...textFieldProps } = extraProps;
    const dateInputProps = type === 'datetime-local' && !allowFuture ? { max: maxDateTime() } : {};
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        type={type}
        value={form[field]}
        onChange={(event) => setForm({ ...form, [field]: event.target.value })}
        InputLabelProps={type === 'datetime-local' ? { shrink: true } : undefined}
        inputProps={type === 'datetime-local'
          ? {
            ...dateInputProps,
            step: 60,
            inputMode: 'numeric',
            ...(inputProps || {})
          }
          : inputProps}
        {...textFieldProps}
      />
    );
  };

  const renderDateTimeInput = (form, setForm, field, label, pickerProps = {}) => (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateTimePicker
        label={label}
        value={parseSupportDateTime(form[field])}
        onChange={(value) => setForm({
          ...form,
          [field]: value && value.isValid()
            ? value.clone().tz(getStoredUserTimezone()).format(DATE_FORMAT.unixDateTime)
            : ''
        })}
        ampm
        views={['year', 'month', 'day', 'hours', 'minutes']}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        )}
        {...pickerProps}
      />
    </LocalizationProvider>
  );

  const renderSiteCountryStateFields = () => (
    <>
      <Grid item xs={12} sm={6}>
        <CountryDropdown
          value={siteForm.country || ''}
          valueType="short"
          labelType="full"
          defaultOptionLabel="Select Country *"
          onChange={(value) => setSiteForm({
            ...siteForm,
            country: value,
            state: ''
          })}
          className="csp-country-select"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <RegionDropdown
          country={siteForm.country || ''}
          countryValueType="short"
          value={siteForm.state || ''}
          valueType="short"
          labelType="full"
          defaultOptionLabel="Select State *"
          disableWhenEmpty
          onChange={(value) => setSiteForm({
            ...siteForm,
            state: value
          })}
          className="csp-country-select"
        />
      </Grid>
    </>
  );

  const renderBitCheckbox = (form, setForm, field, option, length) => (
    <FormControlLabel
      key={option[0]}
      control={(
        <Checkbox
          size="small"
          checked={bitStringHas(form[field], option[0])}
          onChange={() => setForm({
            ...form,
            [field]: toggleBitString(form[field], option[0], length)
          })}
        />
      )}
      label={option[1]}
    />
  );

  const renderCardFields = (form, setForm, dispenserOptions, vehicleOptions, operatorOptions, driverOptions, showStatus = false) => (
    <Stack spacing={2.5}>
      {showStatus && (
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={form.active ? 'true' : 'false'}
            onChange={(event) => setForm({ ...form, active: event.target.value === 'true' })}
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
      )}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Step 1</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'site', 'Select Site *', lookups.sites, {
              resetOnChange: {
                dispenser_ids: [],
                vehicle_id: '',
                operator_id: '',
                vehicle_driver_id: ''
              }
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'vehicle_id', 'Select Vehicle', vehicleOptions, { disabled: !form.site })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'dispenser_ids', 'Select Dispenser *', dispenserOptions, { multiple: true })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'operator_id', 'Select Operator', operatorOptions, { disabled: !form.site })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'vehicle_driver_id', 'Select Vehicle Driver', driverOptions, { disabled: !form.site })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'card_type', 'Card Type *', CARD_TYPES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'interface', 'Card Interface *', CARD_INTERFACES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'card_category', 'Card Category *', CARD_CATEGORIES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'fuel_type', 'Fuel Type', ARTICLE_TYPE)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTextInput(form, setForm, 'uid', 'Card UID *', 'text', { disabled: showStatus })}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Step 2</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSelectField(form, setForm, 'fill_limit_type', 'Per Fill Limit Type *', FILL_LIMIT_TYPES)}
          </Grid>
          {[
            ['per_filling_limit', 'Per Filling Limit *', 'number'],
            ['per_day_limit', 'Per Day Limit *', 'number'],
            ['limit_quantity', 'Limit Quantity *', 'number'],
            ['limit_start_date', 'Limit Start Date', 'datetime-local'],
            ['limit_end_date', 'Limit End Date', 'datetime-local'],
          ].map(([field, label, type = 'text']) => (
            <Grid item xs={12} md={6} key={field}>
              {renderTextInput(
                form,
                setForm,
                field,
                label,
                type,
                type === 'datetime-local' ? { allowFuture: true } : {}
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Step 3</Typography>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Product Table</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ flexWrap: 'wrap' }}>
              {PRODUCT_TABLE_OPTIONS.map((option) => renderBitCheckbox(form, setForm, 'product_table', option, 5))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Manual Inputs</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ flexWrap: 'wrap' }}>
              {MANUAL_INPUT_OPTIONS.map((option) => renderBitCheckbox(form, setForm, 'manual_inputs', option, 4))}
            </Stack>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderSelectField(form, setForm, 'odometer_info', 'Odometer Info', ODOMETER_INFO_OPTIONS)}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={Boolean(form.pin_select)} onChange={(event) => setForm({ ...form, pin_select: event.target.checked })} />}
                label="PIN"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextInput(form, setForm, 'pin', 'PIN Number *')}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextInput(form, setForm, 'wrong_pin_count', 'PIN Error Counter *', 'number')}
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Stack>
  );

  const renderTankFields = () => (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {renderSelectField(tankForm, setTankForm, 'site', 'Select Site *', lookups.sites)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderSelectField(tankForm, setTankForm, 'type', 'Tank Type *', TANK_TYPE)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTextInput(tankForm, setTankForm, 'name', 'Tank Name *')}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTextInput(tankForm, setTankForm, 'code', 'Tank Code *')}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderSelectField(tankForm, setTankForm, 'article', 'Fuel Type *', ARTICLE_TYPE)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTextInput(tankForm, setTankForm, 'sensor_id', 'Sensor Id *')}
        </Grid>
        {[
          ['max_volume', 'Max Volume *', 'number'],
          ['min_volume', 'Min Volume *', 'number'],
          ['max_level', 'Max Level *', 'number'],
          ['min_level', 'Min Level *', 'number'],
        ].map(([field, label, type]) => (
          <Grid item xs={12} md={6} key={field}>
            {renderTextInput(tankForm, setTankForm, field, label, type)}
          </Grid>
        ))}
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={<Switch checked={Boolean(tankForm.automation)} onChange={(event) => setTankForm({ ...tankForm, automation: event.target.checked })} />}
            label="Enable Automation"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={<Switch checked={Boolean(tankForm.fuel_level_only)} onChange={(event) => setTankForm({ ...tankForm, fuel_level_only: event.target.checked })} />}
            label="Fuel Level Only"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={<Switch checked={Boolean(tankForm.fleet_provider_enabled)} onChange={(event) => setTankForm({ ...tankForm, fleet_provider_enabled: event.target.checked })} />}
            label="Enable Fleet Provider Stock"
          />
        </Grid>
        {tankForm.fleet_provider_enabled && (
          <>
            <Grid item xs={12} md={6}>
              {renderSelectField(tankForm, setTankForm, 'fleet_service_provider', 'Fleet Service Provider *', TANK_FLEET_SERVICE_PROVIDER_TYPES)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextInput(tankForm, setTankForm, 'fleet_sensor_name', 'Sensor Name *')}
            </Grid>
            {['OMNICOMM', 'OMINCOMM'].includes(String(tankForm.fleet_service_provider || '').toUpperCase()) && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={Boolean(tankForm.sensor_inward_enabled)} onChange={(event) => setTankForm({ ...tankForm, sensor_inward_enabled: event.target.checked })} />}
                  label="Use Sensor Filling As Inward"
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Stack>
  );

  const renderDispenserFields = () => (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Dispenser Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'site', 'Select Site *', lookups.sites, { resetOnChange: { tank_id: '' } })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'tank_id', 'Select Tank *', dispenserTankOptions)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTextInput(dispenserForm, setDispenserForm, 'station_name', 'Dispenser Name *')}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTextInput(dispenserForm, setDispenserForm, 'address', 'Serial Number *')}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'mode', 'Dispenser Mode *', DISPENSER_MODES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTextInput(dispenserForm, setDispenserForm, 'vehicle_number', 'Vehicle Number')}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Dispenser Configuration</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'dispenser_mount', 'Dispenser Mount', DISPENSER_MOUNTS)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'dp_interface', 'DP Interface', DP_INTERFACES)}
          </Grid>
          {[
            ['dp_no', 'DP No/Nozzle No', 'number'],
            ['dp_address', 'DP Address', 'number'],
            ['sequential_dispenser_id', 'Sequential Dispenser ID', 'number'],
            ['kilometer_difference', 'Kilometer Difference', 'number'],
            ['online_connection_timeout', 'Online Connection Timeout', 'number'],
            ['max_quantity_per_transaction', 'Max Quantity Per Transaction', 'number'],
          ].map(([field, label, type]) => (
            <Grid item xs={12} md={6} key={field}>
              {type === 'datetime-local'
                ? renderDateTimeInput(dispenserForm, setDispenserForm, field, label)
                : renderTextInput(dispenserForm, setDispenserForm, field, label, type)}
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Counter Configuration</Typography>
        <Grid container spacing={2}>
          {[
            ['totalizer_start_reading', 'Totalizer Start Reading', 'number'],
            ['density_start_reading', 'Density Start Reading', 'number'],
            ['volume_decimal', 'Volume Decimal', 'number'],
            ['amount_decimal', 'Amount Decimal', 'number'],
            ['unit_price_decimal', 'Unit Price Decimal', 'number'],
            ['empty_fill_allowed', 'Empty Filling Allowed', 'number'],
          ].map(([field, label, type]) => (
            <Grid item xs={12} md={6} key={field}>
              {renderTextInput(dispenserForm, setDispenserForm, field, label, type)}
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Meter Configuration</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'article', 'Article *', ARTICLE_TYPE)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'pulse_type', 'Pulse Type *', PULSE_TYPES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'pulse_direction', 'Pulse Direction *', PULSE_DIRECTIONS)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'throttle_stage', 'Throttle Stage *', THROTTLE_STAGES)}
          </Grid>
          {[
            ['article_unit', 'Article Unit *'],
            ['price', 'Article Price *', 'number'],
            ['throttle_volume', 'Throttle Volume', 'number'],
            ['min_flow_rate', 'Min Flow Rate', 'number'],
            ['max_flow_rate', 'Max Flow Rate', 'number'],
            ['kfactor', 'Meter Factor', 'number'],
          ].map(([field, label, type = 'text']) => (
            <Grid item xs={12} md={6} key={field}>
              {renderTextInput(
                dispenserForm,
                setDispenserForm,
                field,
                label,
                type,
                type === 'datetime-local' ? { allowFuture: true } : {}
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Timer Configuration</Typography>
        <Grid container spacing={2}>
          {[
            ['max_fueling_time', 'Max Fuelling Time', 'number'],
            ['fuel_break_time', 'Fuel Break Time', 'number'],
            ['fuel_start_time', 'Fuel Start Time', 'number'],
            ['odometer_timeout', 'Odometer Timeout', 'number'],
            ['add_input_timeout', 'Add Input Timeout', 'number'],
            ['pin_entry_timeout', 'Pin Entry Timeout', 'number'],
            ['nozzle_timeout', 'Nozzle Timeout', 'number'],
            ['nozzle_id', 'Nozzle ID'],
          ].map(([field, label, type = 'text']) => (
            <Grid item xs={12} md={6} key={field}>
              {renderTextInput(
                dispenserForm,
                setDispenserForm,
                field,
                label,
                type,
                type === 'datetime-local' ? { allowFuture: true } : {}
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Additional Configuration</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'atg_type', 'ATG Type *', ATG_TYPES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelectField(dispenserForm, setDispenserForm, 'max_digits_of_entry', 'Minimum Digits of Entry', MIN_DIGITS_OPTIONS)}
          </Grid>
          {[
            ['date_time', 'Date/Time *', 'datetime-local'],
            ['tax_percent', 'Tax Percent', 'number'],
            ['alarm', 'Alarms', 'number'],
            ['shift_start_time', 'Shift Start Time', 'number'],
            ['shift_duration', 'Shift Duration', 'number'],
            ['password1', 'Password 1'],
            ['password2', 'Password 2'],
            ['updated_field', 'Updated Field'],
          ].map(([field, label, type = 'text']) => (
            <Grid item xs={12} md={6} key={field}>
              {type === 'datetime-local'
                ? renderDateTimeInput(dispenserForm, setDispenserForm, field, label)
                : renderTextInput(dispenserForm, setDispenserForm, field, label, type)}
            </Grid>
          ))}
          {[
            ['atg_connection', 'ATG Connection'],
            ['printer', 'Enable Printer'],
            ['location', 'Enable Location'],
            ['card_reader', 'Enable Card Reader'],
            ['fueling_with_id_input', 'Fuelling with ID Input'],
            ['back_light', 'Enable Backlight'],
            ['date_time_sync', 'Date/Time Sync'],
            ['status', 'Status'],
          ].map(([field, label]) => (
            <Grid item xs={12} md={6} key={field}>
              <FormControlLabel
                control={<Switch checked={Boolean(dispenserForm[field])} onChange={(event) => setDispenserForm({ ...dispenserForm, [field]: event.target.checked })} />}
                label={label}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  );

  const renderRows = () => {
    const columns = getDisplayColumns(activeTab, selectedCompanyInfo);
    const editableTabs = {
      issue: hasPermission(FEATURE.issueUpdate),
      inward: hasPermission(FEATURE.inwardUpdate),
      sites: hasPermission(FEATURE.siteUpdate),
      vehicles: hasPermission(FEATURE.vehicleUpdate),
      tanks: hasPermission(FEATURE.tankUpdate),
      dispensers: hasPermission(FEATURE.dispenserUpdate),
      cards: hasPermission(FEATURE.cardUpdate)
    };
    const hasActions = activeTab === 'sites'
      ? hasPermission(FEATURE.siteUpdate)
      : ['issue', 'inward', 'vehicles', 'tanks', 'dispensers', 'cards'].includes(activeTab);
    if (tableLoading) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length + (hasActions ? 1 : 0)}>
            <Loader sx={{ minHeight: 180 }} />
          </TableCell>
        </TableRow>
      );
    }
    if (!rows.length) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length + (hasActions ? 1 : 0)} align="center" sx={{ py: 5 }}>
            <Typography color="text.secondary">No records found</Typography>
          </TableCell>
        </TableRow>
      );
    }
    return rows.map((row) => (
      <TableRow hover key={row.id || `${row.address}-${row.number}`}>
        {columns.map(([key, label, type, fallbackKey]) => (
          <TableCell key={label}>
            {formatValue(
              row[key],
              type,
              selectedCompanyInfo?.fuel_volume_unit,
              fallbackKey ? row[fallbackKey] : undefined
            )}
          </TableCell>
        ))}
        {hasActions && (
          <TableCell align="center" sx={{ minWidth: 260 }}>
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              sx={{ py: 0.5 }}
            >
              {activeTab !== 'sites' && activeTab !== 'inward' && activeTab !== 'issue' && (
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => openDetailDialog(activeTab, row)}
                  sx={{ textTransform: 'none' }}
                >
                  Details
                </Button>
              )}
              {editableTabs[activeTab] && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    if (activeTab === 'sites') openSiteDialog(row);
                    if (activeTab === 'issue') openIssueDialog(row);
                    if (activeTab === 'inward') openInwardDialog(row);
                    if (activeTab === 'vehicles') openVehicleDialog(row);
                    if (activeTab === 'tanks') openTankDialog(row);
                    if (activeTab === 'dispensers') openDispenserDialog(row);
                    if (activeTab === 'cards') openCardDialog(row);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Edit
                </Button>
              )}
              {activeTab === 'cards' && hasPermission(FEATURE.cardUpdate) && (
                <Button
                  size="small"
                  startIcon={<SyncIcon />}
                  onClick={() => openCardSyncStatus(row)}
                  sx={{ textTransform: 'none' }}
                >
                  Sync Status
                </Button>
              )}
              {activeTab === 'dispensers' && hasPermission(FEATURE.dispenserUpdate) && (
                <>
                  <Button
                    size="small"
                    startIcon={<LinkIcon />}
                    onClick={() => {
                      setLinkedDispenserId(row.id);
                      setLinkedCardsOpen(true);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Linked Cards
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ResetIcon />}
                    onClick={() => {
                      setResetDispenserId(row.id);
                      setResetConfirmOpen(true);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Reset Cards
                  </Button>
                </>
              )}
            </Stack>
          </TableCell>
        )}
      </TableRow>
    ));
  };

  const renderTable = () => {
    const columns = getDisplayColumns(activeTab, selectedCompanyInfo);
    const hasActions = activeTab === 'sites'
      ? hasPermission(FEATURE.siteUpdate)
      : ['issue', 'inward', 'vehicles', 'tanks', 'dispensers', 'cards'].includes(activeTab);
    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          flex: 1,
          minHeight: 0
        }}
      >
        <TableContainer sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 430px)' } }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map(([, label]) => (
                  <TableCell key={label} sx={{ bgcolor: '#F9FAFB', fontWeight: 800 }}>
                    {label}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell align="center" sx={{ bgcolor: '#F9FAFB', fontWeight: 800, minWidth: 260 }}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>{renderRows()}</TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page} rowsPerPage={limit} count={totalCount} onPageChange={handlePageChange} />
      </Paper>
    );
  };

  if (!companies.length) {
    return (
      <>
        <ScreenHeader title="Support" />
        <Box sx={{ bgcolor: '#F6F8FB', minHeight: 'calc(100vh - 64px)', py: 3 }}>
          <Container maxWidth="lg">
            <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>No company assigned</Typography>
              <Typography color="text.secondary">
                Contact administrator to assign company access for this support account.
              </Typography>
            </Paper>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Support | Retrack FMS</title>
      </Helmet>
      <Box sx={{ bgcolor: '#F6F8FB', minHeight: 'calc(100vh - 64px)', py: 2 }}>
        <Container maxWidth="xl" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 2.5 } }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#111827', letterSpacing: 0 }}>
                  Support Workspace
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Company-scoped operational tools for assigned support engineers.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ minWidth: { lg: 620 } }}>
                <FormControl size="small" fullWidth sx={selectMenuSx}>
                  <InputLabel>Company</InputLabel>
                  <Select
                    label="Company"
                    value={selectedCompany}
                    onChange={(event) => setSelectedCompany(event.target.value)}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Start"
                  type="datetime-local"
                  value={dateRange.start_time}
                  onChange={(event) => updateDateRange('start_time', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: maxDateTime() }}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="End"
                  type="datetime-local"
                  value={dateRange.end_time}
                  onChange={(event) => updateDateRange('end_time', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: maxDateTime() }}
                  fullWidth
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip label={selectedCompanyInfo?.name || 'Company'} color="primary" variant="outlined" />
              <Chip label={`${permissions.length} permissions`} variant="outlined" />
              <Chip label={`${lookups.sites?.length || 0} sites`} variant="outlined" />
            </Stack>
          </Paper>

          {loading ? <Loader /> : renderSummary()}

          {!availableTabs.length ? (
            <Alert severity="warning">No support permissions are enabled for this account.</Alert>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                p: { xs: 1.5, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                <Tabs
                  value={activeTab}
                  onChange={(event, value) => setActiveTab(value)}
                  sx={{ minHeight: 42 }}
                >
                  {tabsToRender.map((tab) => (
                    <Tab key={tab.id} label={tab.label} value={tab.id} sx={{ minHeight: 42, textTransform: 'none', fontWeight: 800 }} />
                  ))}
                </Tabs>
                {overflowTabs.length > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      endIcon={<MoreIcon />}
                      onClick={(event) => setMoreTabsAnchor(event.currentTarget)}
                      sx={{ textTransform: 'none', minHeight: 40, whiteSpace: 'nowrap' }}
                      aria-haspopup="true"
                      aria-expanded={moreTabsAnchor ? 'true' : undefined}
                    >
                      More
                    </Button>
                    <Menu
                      anchorEl={moreTabsAnchor}
                      open={Boolean(moreTabsAnchor)}
                      onClose={() => setMoreTabsAnchor(null)}
                    >
                      {overflowTabs.map((tab) => (
                        <MenuItem
                          key={tab.id}
                          selected={activeTab === tab.id}
                          onClick={() => {
                            setActiveTab(tab.id);
                            setMoreTabsAnchor(null);
                          }}
                        >
                          {tab.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                {renderToolbarActions()}
              </Stack>
              <Divider />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 }, ...selectMenuSx }}>
                  <InputLabel>Site</InputLabel>
                  <Select label="Site" value={siteFilter} onChange={(event) => setSiteFilter(event.target.value)}>
                    {siteOptions.map((site) => (
                      <MenuItem key={site.id} value={site.id}>{site.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {activeTab === 'vehicles' && (
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 }, ...selectMenuSx }}>
                    <InputLabel>Group</InputLabel>
                    <Select
                      label="Group"
                      value={vehicleGroupFilter}
                      onChange={(event) => setVehicleGroupFilter(event.target.value)}
                    >
                      <MenuItem value="all">All Groups</MenuItem>
                      {lookups.vehicle_groups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  size="small"
                  placeholder={searchPlaceholderByTab[activeTab] || 'Search records'}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  sx={{ width: { xs: '100%', md: 320 } }}
                />
              </Stack>
              {renderTable()}
            </Paper>
          )}
        </Container>
      </Box>

      <Dialog open={exportFilterOpen} onClose={() => setExportFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{`Export ${activeTabLabel} Transactions`}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              {renderSelectField(
                exportFilterForm,
                setExportFilterForm,
                'site',
                'Site',
                siteOptions,
                { resetOnChange: { tank: 'all' } }
              )}
            </Grid>
            {['issue', 'inward'].includes(activeTab) && (
              <Grid item xs={12}>
                {renderSelectField(exportFilterForm, setExportFilterForm, 'tank', 'Tank', exportTankOptions)}
              </Grid>
            )}
            {['issue', 'transfer'].includes(activeTab) && (
              <>
                <Grid item xs={12}>
                  {renderSelectField(exportFilterForm, setExportFilterForm, 'address', 'Dispenser', exportDispenserOptions)}
                </Grid>
                <Grid item xs={12}>
                  {renderSelectField(exportFilterForm, setExportFilterForm, 'vehicle_number', 'Vehicle', exportVehicleOptions)}
                </Grid>
                <Grid item xs={12}>
                  {renderSelectField(exportFilterForm, setExportFilterForm, 'vehicle_group', 'Vehicle Group', [
                    { id: 'all', name: 'All' },
                    ...lookups.vehicle_groups
                  ])}
                </Grid>
              </>
            )}
            {activeTab === 'issue' && (
              <Grid item xs={12}>
                {renderSelectField(exportFilterForm, setExportFilterForm, 'asset_type', 'Asset Type', [
                  { id: 'all', name: 'All' },
                  ...CONSUMER_VEHICLE_TYPES
                ])}
              </Grid>
            )}
            {activeTab === 'external' ? (
              <Grid item xs={12}>
                {renderDateTimeInput(exportFilterForm, setExportFilterForm, 'date_time', 'Date')}
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  {renderDateTimeInput(exportFilterForm, setExportFilterForm, 'start_time', 'Start Date')}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderDateTimeInput(exportFilterForm, setExportFilterForm, 'end_time', 'End Date')}
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={0.75}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Quick date range
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {[
                        ['today', 'Today'],
                        ['yesterday', 'Yesterday'],
                        ['last-7-days', 'Last 7 days'],
                        ['last-30-days', 'Last 30 days']
                      ].map(([presetId, label]) => (
                        <Button
                          key={presetId}
                          type="button"
                          variant="outlined"
                          size="small"
                          onClick={() => applyExportDatePreset(presetId)}
                          sx={{ textTransform: 'none' }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportFilterOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExportTransactions}
            disabled={transactionExporting}
            sx={{ textTransform: 'none' }}
          >
            {transactionExporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={issueOpen} onClose={() => { setIssueOpen(false); setEditingRecord(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Update Issue Transaction</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {[
              ['odometer_entry', 'Odometer *', 'number'],
              ['vehicle_number', 'Vehicle Number *'],
              ...(selectedCompanyInfo?.asset_code_mapping_enabled
                ? [['additional_entry', 'Additional Entry *', 'text']]
                : [])
            ].map(([field, label, type = 'text']) => (
              <TextField
                key={field}
                fullWidth
                size="small"
                label={label}
                type={type}
                value={issueForm[field]}
                onChange={(event) => {
                  setIssueForm({ ...issueForm, [field]: event.target.value });
                  if (issueErrors[field]) {
                    setIssueErrors((current) => ({ ...current, [field]: '' }));
                  }
                }}
                inputProps={type === 'number' ? { min: 0, step: 'any' } : undefined}
                error={Boolean(issueErrors[field])}
                helperText={issueErrors[field] || ''}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIssueOpen(false); setEditingRecord(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveIssue} sx={{ textTransform: 'none' }}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inwardOpen} onClose={() => { setInwardOpen(false); setEditingRecord(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord?.type === 'inward' ? 'Update Inward Transaction' : 'Add Inward Transaction'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Transaction Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderSelectField(inwardForm, setInwardForm, 'site', 'Select Site *', lookups.sites)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSelectField(inwardForm, setInwardForm, 'supplier', 'Select Supplier *', lookups.suppliers)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSelectField(inwardForm, setInwardForm, 'received_tank', 'Select Output Tank *', inwardTankOptions)}
                </Grid>
                {[
                  ['quantity', `Fuel Quantity${inwardVolumeLabel} *`, 'number'],
                  ['received_station', 'Received Station'],
                  ['price_per_litre', 'Price Per Litre *', 'number'],
                  ['invoice_number', 'Invoice Number *'],
                  ['order_date', 'Order Date/Time *', 'datetime-local'],
                  ['delivery_date', 'Delivery Date/Time *', 'datetime-local'],
                  ['delivery_vehicle_number', 'Delivery Vehicle Number *'],
                ].map(([field, label, type = 'text']) => (
                  <Grid item xs={12} md={6} key={field}>
                    {type === 'datetime-local'
                      ? renderDateTimeInput(inwardForm, setInwardForm, field, label, {
                        minDateTime: moment().subtract(1, 'month'),
                        maxDateTime: moment()
                      })
                      : renderTextInput(inwardForm, setInwardForm, field, label, type)}
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Additional Details</Typography>
              <Grid container spacing={2}>
                {[
                  ['delivered_dip_quantity', `Delivered Dip Quantity${inwardVolumeLabel} *`, 'number'],
                  ['delivered_measured_quantity_FLS', `Delivered Measured Quantity FLS${inwardVolumeLabel} *`, 'number'],
                  ['delivered_measured_quantity_meter', `Delivered Measured Quantity Meter${inwardVolumeLabel} *`, 'number'],
                  ['temperature_during_loading', 'Temperature During Loading *', 'number'],
                  ['temperature_during_unloading', 'Temperature During Unloading *', 'number'],
                  ['spillage_quantity', `Spillage Quantity${inwardVolumeLabel} *`, 'number'],
                  ['shortage_quantity', `Shortage Quantity${inwardVolumeLabel} *`, 'number'],
                  ['density_during_loading', 'Density During Loading *', 'number'],
                  ['density_during_unloading', 'Density During Unloading *', 'number'],
                ].map(([field, label, type = 'text']) => (
                  <Grid item xs={12} md={6} key={field}>
                    {renderTextInput(inwardForm, setInwardForm, field, label, type)}
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box>
              {renderTextInput(inwardForm, setInwardForm, 'remarks', 'Remarks', 'text', { multiline: true, minRows: 2 })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInwardOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveInward} sx={{ textTransform: 'none' }}>
            {editingRecord?.type === 'inward' ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={siteOpen} onClose={() => { if (!siteSaving) { setSiteOpen(false); setEditingRecord(null); } }} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord?.type === 'sites' ? 'Update Site' : 'Add Site'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {[
              ['name', 'Site Name *'],
              ['code', 'Site Code *'],
              ['city', 'City *'],
              ['pin_code', 'Pincode *']
            ].map(([field, label]) => (
              <Grid item xs={12} sm={6} key={field}>
                {renderTextInput(siteForm, setSiteForm, field, label)}
              </Grid>
            ))}
            {renderSiteCountryStateFields()}
            <Grid item xs={12} sm={6}>
              {renderSelectField(siteForm, setSiteForm, 'timezone', 'Timezone *', TIMEZONE_OPTIONS)}
            </Grid>
            <Grid item xs={12}>
              {renderTextInput(siteForm, setSiteForm, 'address_line1', 'Address *')}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={Boolean(siteForm.active)} onChange={(event) => setSiteForm({ ...siteForm, active: event.target.checked })} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button disabled={siteSaving} onClick={() => { setSiteOpen(false); setEditingRecord(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button disabled={siteSaving} variant="contained" onClick={handleSaveSite} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={vehicleOpen} onClose={() => { setVehicleOpen(false); setEditingRecord(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord?.type === 'vehicles' ? 'Update Vehicle' : 'Add Vehicle'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'site', 'Select Site *', lookups.sites, {
                resetOnChange: { tank: '' }
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'type', 'Vehicle Type *', (
                editingRecord?.type === 'vehicles' && vehicleForm.type === LEGACY_SEMI_MOVABLE_OPTION.id
                  ? [...VEHICLE_TYPE_FORM_OPTIONS, LEGACY_SEMI_MOVABLE_OPTION]
                  : VEHICLE_TYPE_FORM_OPTIONS
              ))}
            </Grid>
            {[
              ['registration_num', 'Registration Number *'],
              ['asset_code', 'Asset Code *'],
              ['tank_capacity', 'Tank Capacity *', 'number'],
              ['model', 'Vehicle Model'],
            ].map(([field, label, type = 'text']) => (
              <Grid item xs={12} md={6} key={field}>
                {renderTextInput(vehicleForm, setVehicleForm, field, label, type)}
              </Grid>
            ))}
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'category', 'Vehicle Category', VEHICLE_CATEGORY, {
                resetOnChange: { tank: '' }
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(
                vehicleForm,
                setVehicleForm,
                'tank',
                vehicleForm.category === 'TRNSFR' ? 'Select Transfer Tank *' : 'Select Transfer Tank',
                vehicleTransferTankOptions,
                { disabled: vehicleForm.category !== 'TRNSFR' }
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'fuel_type', 'Fuel Type *', ARTICLE_TYPE)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'group', 'Vehicle Group', lookups.vehicle_groups)}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={Boolean(vehicleForm.telemetics_enabled)} onChange={(event) => setVehicleForm({ ...vehicleForm, telemetics_enabled: event.target.checked })} />}
                label="Telematics Enabled"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(
                vehicleForm,
                setVehicleForm,
                'telemetics_category',
                vehicleForm.telemetics_enabled ? 'Telematics Category *' : 'Telematics Category',
                TELEMATICS_TYPES,
                { disabled: !vehicleForm.telemetics_enabled }
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(
                vehicleForm,
                setVehicleForm,
                'fleet_service_provider',
                'Fleet Service Provider',
                FLEET_SERVICE_PROVIDER_TYPES,
                { disabled: !vehicleForm.telemetics_enabled }
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'department', 'Department', lookups.vehicle_departments)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'contractor', 'Contractor', lookups.vehicle_contractors)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSelectField(vehicleForm, setVehicleForm, 'consumer_vehicle', 'Asset Type', CONSUMER_VEHICLE_TYPES)}
            </Grid>
            {[
              ['odometer_reading', 'Odometer Reading *', 'number'],
              ['standard_mileage', 'Standard Mileage', 'number'],
              ['po_number', 'PO Number'],
              ['monthly_fixed_engine_hours', 'Monthly Fixed Engine Hours', 'number'],
              ['service_interval', 'Service Interval', 'number'],
              ['max_diff_allowed', 'Max Diff Allowed', 'number'],
              ['omnicomm_secondary_engine_threshold', 'Secondary Engine Threshold', 'number'],
            ].map(([field, label, type = 'text']) => (
              <Grid item xs={12} md={6} key={field}>
                {renderTextInput(vehicleForm, setVehicleForm, field, label, type)}
              </Grid>
            ))}
            <Grid item xs={12}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {[
                  ['show_omnicomm_log_ignition', 'Ignition'],
                  ['show_omnicomm_log_engine', 'Engine'],
                  ['show_omnicomm_log_secondary_engine', 'Secondary Engine'],
                ].map(([field, label]) => (
                  <FormControlLabel
                    key={field}
                    control={(
                      <Switch
                        checked={Boolean(vehicleForm[field])}
                        onChange={(event) => setVehicleForm({ ...vehicleForm, [field]: event.target.checked })}
                      />
                    )}
                    label={label}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVehicleOpen(false); setEditingRecord(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveVehicle} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tankOpen} onClose={() => { setTankOpen(false); setEditingRecord(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord?.type === 'tanks' ? 'Update Tank' : 'Add Tank'}</DialogTitle>
        <DialogContent dividers>
          {renderTankFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTankOpen(false); setEditingRecord(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTank} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dispenserOpen} onClose={() => { setDispenserOpen(false); setEditingRecord(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord?.type === 'dispensers' ? 'Update Dispenser' : 'Add Dispenser'}</DialogTitle>
        <DialogContent dividers>
          {renderDispenserFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDispenserOpen(false); setEditingRecord(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDispenser} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cardOpen} onClose={() => setCardOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Card</DialogTitle>
        {cardForm && (
          <DialogContent dividers>
            <Stack spacing={2}>
              <Alert severity="info">Changes mark linked dispensers for sync.</Alert>
              {renderCardFields(
                cardForm,
                setCardForm,
                cardFormDispenserOptions,
                cardFormVehicleOptions,
                cardFormOperatorOptions,
                cardFormDriverOptions,
                true
              )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setCardOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateCard} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cardCreateOpen} onClose={() => { if (!cardCreateSaving) setCardCreateOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Add Card</DialogTitle>
        <DialogContent dividers>
          {renderCardFields(
            newCardForm,
            setNewCardForm,
            newCardDispenserOptions,
            newCardVehicleOptions,
            newCardOperatorOptions,
            newCardDriverOptions
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={cardCreateSaving} onClick={() => setCardCreateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button disabled={cardCreateSaving} variant="contained" onClick={handleCreateCard} sx={{ textTransform: 'none' }}>Save</Button>
        </DialogActions>
      </Dialog>

      <CardSyncStatusModal
        open={cardSyncOpen}
        onClose={() => {
          setCardSyncOpen(false);
          setCardSyncRecord(null);
        }}
        data={cardSyncRecord}
        onRemoveDispenser={removeSupportCardDispenser}
        onDispenserClick={handleSupportDispenserClick}
      />

      <DispenserLinkedCardsModel
        open={linkedCardsOpen}
        onClose={() => {
          setLinkedCardsOpen(false);
          setLinkedDispenserId(null);
        }}
        dispenserId={linkedDispenserId}
        fetchCards={fetchSupportLinkedCards}
      />

      <NewMap
        open={mapCardsOpen}
        handleClose={() => setMapCardsOpen(false)}
        submitMap={handleMapSupportCards}
      />

      <ConfirmationDialog
        open={resetConfirmOpen}
        info="Are you sure you want to reset all cards for this dispenser?"
        actionName="Reset Cards"
        onClose={() => {
          setResetConfirmOpen(false);
          setResetDispenserId(null);
        }}
        onSubmit={handleResetDispenserCards}
      />

      <Dialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailRecord(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {detailRecord?.type ? `${formatDetailLabel(detailRecord.type)} Details` : 'Details'}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Loader sx={{ minHeight: 220 }} />
          ) : (
            <Grid container spacing={1.5}>
              {Object.entries(detailRecord?.data || {})
                .filter(([key]) => !key.endsWith('_gallons'))
                .map(([key, value]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 1.5, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {formatDetailLabel(key)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.75, wordBreak: 'break-word' }}>
                        {formatDetailValue(key, value, selectedCompanyInfo?.fuel_volume_unit)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDetailOpen(false);
              setDetailRecord(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupportPortal;
